import React, { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload,
  faSpinner,
  faWandMagicSparkles,
  faDownload,
  faCopy,
  faStop,
  faFileLines,
  faClipboardCheck,
  faListCheck,
  faPenToSquare,
  faTriangleExclamation,
  faTrash,
  faEye,
  faImages,
  faPlus,
  faChevronRight,
  faChevronDown,
  faArrowsRotate,
  faFont,
  faExpand,
  faCompress,
  faXmark,
  faChevronLeft,
  faFloppyDisk,
} from '@fortawesome/free-solid-svg-icons';
import plansPrepApi, { downloadBlob, downloadText } from '../utils/plansPrepApi';
import { LOCAL_MODELS } from '../utils/localModels';
import { useAuth } from '../contexts/AuthContext';
import './PlansPreparationsTab.css';

const GRADES = Array.from({ length: 12 }, (_, i) => i + 1);
const PP_PREFS_KEY = 'plansPrep.selection.v1';
const PP_PROVIDER_KEY = 'plansPrep.provider.v1'; // legacy → notes
const PP_LOCAL_MODEL_KEY = 'plansPrep.localModel.v1'; // legacy → notes
const PP_LOCAL_HOST_KEY = 'plansPrep.localHost.v1'; // legacy → notes
const PP_OCR_PROVIDER_KEY = 'plansPrep.ocrProvider.v1';
const PP_OCR_LOCAL_MODEL_KEY = 'plansPrep.ocrLocalModel.v1';
const PP_OCR_LOCAL_HOST_KEY = 'plansPrep.ocrLocalHost.v1';
const PP_NOTES_PROVIDER_KEY = 'plansPrep.notesProvider.v1';
const PP_NOTES_LOCAL_MODEL_KEY = 'plansPrep.notesLocalModel.v1';
const PP_NOTES_LOCAL_HOST_KEY = 'plansPrep.notesLocalHost.v1';

function readStoredEngine(primaryKey, legacyKey, fallback = 'cursor') {
  try {
    const v = localStorage.getItem(primaryKey) || localStorage.getItem(legacyKey);
    if (v === 'local' || v === 'cursor' || v === 'claude') return v;
  } catch (_) { /* ignore */ }
  return fallback;
}

function readStoredLocalModel(primaryKey, legacyKey) {
  try {
    const v = localStorage.getItem(primaryKey) || localStorage.getItem(legacyKey);
    if (v && LOCAL_MODELS.some((m) => m.value === v)) return v;
  } catch (_) { /* ignore */ }
  return LOCAL_MODELS[0]?.value || 'ggml';
}

function readStoredLocalHost(primaryKey, legacyKey) {
  try {
    const v = localStorage.getItem(primaryKey) || localStorage.getItem(legacyKey);
    if (v) return v;
  } catch (_) { /* ignore */ }
  return 'mac';
}

function readStoredOcrProvider() {
  return readStoredEngine(PP_OCR_PROVIDER_KEY, PP_PROVIDER_KEY, 'cursor');
}
function readStoredNotesProvider() {
  return readStoredEngine(PP_NOTES_PROVIDER_KEY, PP_PROVIDER_KEY, 'cursor');
}
function readStoredOcrLocalModel() {
  return readStoredLocalModel(PP_OCR_LOCAL_MODEL_KEY, PP_LOCAL_MODEL_KEY);
}
function readStoredNotesLocalModel() {
  return readStoredLocalModel(PP_NOTES_LOCAL_MODEL_KEY, PP_LOCAL_MODEL_KEY);
}
function readStoredOcrLocalHost() {
  return readStoredLocalHost(PP_OCR_LOCAL_HOST_KEY, PP_LOCAL_HOST_KEY);
}
function readStoredNotesLocalHost() {
  return readStoredLocalHost(PP_NOTES_LOCAL_HOST_KEY, PP_LOCAL_HOST_KEY);
}

function readPpPrefs() {
  try {
    const raw = localStorage.getItem(PP_PREFS_KEY);
    if (!raw) return {};
    const j = JSON.parse(raw);
    return j && typeof j === 'object' ? j : {};
  } catch (_) {
    return {};
  }
}

function writePpPrefs(patch) {
  try {
    const next = { ...readPpPrefs(), ...patch, savedAt: Date.now() };
    localStorage.setItem(PP_PREFS_KEY, JSON.stringify(next));
  } catch (_) { /* ignore quota / private mode */ }
}

function looksUrdu(text) {
  return /[\u0600-\u06FF]/.test(String(text || '').slice(0, 800));
}

/** Prefer worksheet for workbook subjects, else notes / test / mcq. */
function preferredDayStep(day) {
  const steps = day?.steps || [];
  const order = ['worksheet', 'notes', 'test', 'mcq'];
  for (const key of order) {
    const s = steps.find((x) => x.key === key && x.done);
    if (s) return key;
  }
  const any = steps.find((x) => x.done);
  return any?.key || null;
}

function dayProgress(day) {
  const steps = day?.steps || [];
  const total = steps.length;
  const done = steps.filter((s) => s.done).length;
  return { done, total };
}

/** previewKey shapes: `day:3:notes` | `day:3:topic_ocr` | `plan` | `full_notes` … */
function parsePreviewSelection(previewKey) {
  const m = /^day:(\d+)(?::([a-z_]+))?$/i.exec(String(previewKey || ''));
  if (!m) return null;
  return { day: parseInt(m[1], 10), step: m[2] ? m[2].toLowerCase() : null };
}

const NOTES_ANSWER_PREVIEW_KEYS = new Set([
  'full_notes', 'full_short', 'full_long', 'exercise_notes', 'qb_notes',
]);

function isNotesAnswerPreview(previewKey) {
  if (NOTES_ANSWER_PREVIEW_KEYS.has(previewKey)) return true;
  const sel = parsePreviewSelection(previewKey);
  return !!(sel && sel.step === 'notes');
}

/** Bold the "Ans:" label only. The answer body stays regular text. */
function answerPreviewLine(label, after) {
  const body = String(after || '').trim();
  if (!body) return `**${label}:**`;
  return `**${label}:** ${body}`;
}

/**
 * Text preview only. Stored notes keep **Ans.**; that form sits on the question
 * line and does not render as bold when the answer word is glued on (**Ans.**Acids).
 * Show "Ans:" in bold, with a colon, on its own line. The answer body stays regular.
 */
function formatNotesAnswerPreview(markdown) {
  const src = String(markdown || '').replace(/\r\n/g, '\n');
  if (!src.trim()) return src;
  const markerRe = /\*\*\s*(Ans|جواب)\s*(?:[.:]\s*\*\*|\*\*\s*[.:]?)/gi;
  const lines = src.split('\n');
  const out = [];
  let inFence = false;

  const pushAnswer = (piece) => {
    if (out.length && out[out.length - 1] !== '') out.push('');
    out.push(piece);
  };

  const splitLine = (line) => {
    markerRe.lastIndex = 0;
    const m = markerRe.exec(line);
    if (!m) {
      const bare = line.match(/^(.*[?؟])\s+Ans\.\s*(.*)$/i);
      if (!bare) return [line];
      return [bare[1].trimEnd(), answerPreviewLine('Ans', bare[2])];
    }
    let before = line.slice(0, m.index).trimEnd();
    const closedQuestion = !!(before && ((before.match(/\*\*/g) || []).length % 2 === 1));
    if (closedQuestion) before += '**';
    const label = /^جواب$/i.test(m[1]) ? 'جواب' : 'Ans';
    let after = line.slice(m.index + m[0].length).replace(/^[ \t]+/, '').replace(/^[.:][ \t]*/, '');
    if (closedQuestion) after = after.replace(/\*\*\s*$/, '');
    const pieces = [];
    if (before) pieces.push(before);
    if (after.startsWith('|')) {
      pieces.push(`**${label}:**`);
      pieces.push(after);
    } else {
      pieces.push(answerPreviewLine(label, after));
    }
    return pieces;
  };

  for (const line of lines) {
    if (/^\s*```/.test(line)) {
      inFence = !inFence;
      out.push(line);
      continue;
    }
    if (inFence || /^\s*\|/.test(line)) {
      out.push(line);
      continue;
    }
    for (const piece of splitLine(line)) {
      if (/^\*\*(?:Ans|جواب):/.test(piece)) pushAnswer(piece);
      else if (/^\s*\|/.test(piece) && out.length && out[out.length - 1] !== '') {
        out.push('');
        out.push(piece);
      } else out.push(piece);
    }
  }
  return out.join('\n').replace(/\n{3,}/g, '\n\n');
}

/** Inline the preview's bold and table styles so Word keeps them on paste. */
function styleNotesHtmlForWord(root) {
  root.querySelectorAll('h1,h2,h3,h4,strong,b,p,table,th,td').forEach((el) => {
    const tag = el.tagName;
    if (tag === 'STRONG' || tag === 'B') el.style.fontWeight = 'bold';
    if (tag === 'H1' || tag === 'H2' || tag === 'H3' || tag === 'H4') {
      el.style.fontWeight = 'bold';
      el.style.margin = '0.9em 0 0.35em';
    }
    if (tag === 'P') el.style.margin = '0.35em 0';
    if (tag === 'TABLE') {
      el.style.borderCollapse = 'collapse';
      el.style.width = '100%';
      el.setAttribute('border', '1');
      el.setAttribute('cellspacing', '0');
      el.setAttribute('cellpadding', '4');
    }
    if (tag === 'TH' || tag === 'TD') {
      el.style.border = '1px solid #cccccc';
      el.style.padding = '4px 6px';
      el.style.verticalAlign = 'top';
    }
    if (tag === 'TH') {
      el.style.fontWeight = 'bold';
      el.style.backgroundColor = '#f5f5f5';
    }
  });
}

function wordHtmlDocument(fragment, rtl) {
  const body = rtl ? `<div dir="rtl">${fragment}</div>` : fragment;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body><!--StartFragment-->${body}<!--EndFragment--></body></html>`;
}

function notesFragmentHtml(node) {
  const host = document.createElement('div');
  host.innerHTML = node.innerHTML;
  styleNotesHtmlForWord(host);
  return host.innerHTML;
}

function copySelectionOf(node) {
  const selection = document.getSelection();
  if (!selection) return false;
  const previous = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
  const range = document.createRange();
  range.selectNodeContents(node);
  selection.removeAllRanges();
  selection.addRange(range);
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch (_) {
    ok = false;
  } finally {
    selection.removeAllRanges();
    if (previous) {
      try { selection.addRange(previous); } catch (_) { /* ignore */ }
    }
  }
  return ok;
}

async function copyRichHtml(node) {
  // Same clipboard payload as dragging to select the preview, which Word pastes with bold and tables.
  if (copySelectionOf(node)) return;
  const plain = node.innerText || node.textContent || '';
  const rtl = !!(node.closest && node.closest('.rtl')) || node.classList?.contains('rtl');
  const fragment = notesFragmentHtml(node);
  const html = wordHtmlDocument(fragment, rtl);
  const canWrite = typeof navigator !== 'undefined'
    && navigator.clipboard
    && typeof navigator.clipboard.write === 'function'
    && typeof window.ClipboardItem === 'function'
    && window.isSecureContext;
  if (canWrite) {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plain], { type: 'text/plain' }),
      }),
    ]);
    return;
  }
  throw new Error('Clipboard copy is not supported in this browser.');
}

function renderNotesCopyNode(markdown) {
  const host = document.createElement('div');
  host.className = `pp-preview-doc${looksUrdu(markdown) ? ' rtl' : ''}`;
  host.style.cssText = 'position:fixed;left:-10000px;top:0;width:840px;background:#fff;color:#222;';
  document.body.appendChild(host);
  const root = createRoot(host);
  flushSync(() => {
    root.render(
      <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {formatNotesAnswerPreview(markdown)}
      </ReactMarkdown>
    );
  });
  host._notesCopyRoot = root;
  return host;
}

function releaseNotesCopyNode(node) {
  if (node && node._notesCopyRoot) {
    node._notesCopyRoot.unmount();
    node.remove();
  }
}

function pipelineStageLabel(stage) {
  const map = {
    uploaded: 'Uploaded',
    ocr_processing: 'OCR Processing',
    ocr_complete: 'OCR Complete',
    topics_detected: 'Topics Detected',
    structure_empty: 'Structure empty',
    exercise_detected: 'Exercise Detected',
    topic_content_generating: 'Topic Content Generating',
    exercise_solutions_generating: 'Exercise Solutions Generating',
    planning: 'Planning',
    qa_checking: 'QA Checking',
    correction: 'Correction',
    finalizing: 'Finalizing',
    pdf_generating: 'PDF Generating',
    complete: 'Complete',
  };
  return map[stage] || stage || '';
}

function parseMarkdownTableRows(text) {
  const clean = String(text || '').replace(/\r/g, '');
  const lines = clean.split('\n');
  const isSep = (t) => /^\|?[\s:|-]*-{2,}[\s:|-]*\|?$/.test(t) && t.includes('-');
  const isRow = (t) => t.startsWith('|') && (t.match(/\|/g) || []).length >= 2;
  const toCells = (t) => {
    let s = t;
    if (s.startsWith('|')) s = s.slice(1);
    if (s.endsWith('|')) s = s.slice(0, -1);
    return s.split('|').map((c) => c.trim().replace(/\*\*/g, ''));
  };
  const start = lines.findIndex((l) => { const t = l.trim(); return isRow(t) || isSep(t); });
  if (start < 0) return [];
  const out = [];
  for (let i = start; i < lines.length; i++) {
    const t = lines[i].trim();
    if (isSep(t)) continue;
    if (isRow(t)) { out.push(toCells(t)); continue; }
    break;
  }
  return out;
}

function parsePlanTableRows(text) {
  const md = parseMarkdownTableRows(text);
  if (md.length) return md;
  const lines = String(text || '').replace(/\r/g, '').split('\n').map((l) => l.trim()).filter(Boolean);
  const rows = [];
  for (const line of lines) {
    if (!line.includes('\t')) continue;
    rows.push(line.split('\t').map((c) => c.trim().replace(/\*\*/g, '')));
  }
  return rows;
}

function planToEdupageTsv(text, meta = {}) {
  const EDUPAGE_HEADER = ['Lesson', 'Chapter', 'Achievements', 'Topic', 'Notes', 'Cross-Curricular Topics'];
  const rows = parsePlanTableRows(text);
  if (!rows.length) return String(text || '').replace(/\r/g, '').trim();
  const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const header = rows[0].map(norm);
  const col = (...aliases) => {
    for (const a of aliases) {
      const i = header.findIndex((h) => h === a || h.startsWith(a));
      if (i >= 0) return i;
    }
    return -1;
  };
  const lessonIdx = col('lesson');
  const chapterIdx = col('chapter');
  const achIdx = col('achievements', 'achievement');
  const topicIdx = col('topic');
  const notesIdx = col('notes');
  const crossIdx = col('cross-curricular topics', 'cross-curricular');
  const blankish = (v) => {
    const s = String(v || '').trim();
    return !s || s === '-' || s === '—' || s === '–';
  };
  const cell = (row, idx) => (idx >= 0 && row[idx] != null) ? String(row[idx]).trim() : '';
  const formatChapter = (raw) => {
    const s = String(raw || '').replace(/\s+/g, ' ').trim();
    if (!s) return '';
    const m = s.match(/^ch\s*#?\s*(\d+)\s*(.*)$/i);
    if (m) {
      const name = String(m[2] || '').trim();
      return name ? `Ch#${m[1]} ${name}` : `Ch#${m[1]}`;
    }
    return s;
  };
  const sameChapter = (a, b) => {
    const na = String(a || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const nb = String(b || '').replace(/\s+/g, ' ').trim().toLowerCase();
    return Boolean(na && nb && na === nb);
  };
  let pendingChapter = '';
  if (meta.chapterNo != null && String(meta.chapterName || '').trim()) {
    pendingChapter = `Ch#${meta.chapterNo} ${String(meta.chapterName).trim()}`.replace(/\s+/g, ' ');
  }
  let lastChapter = '';
  const out = [EDUPAGE_HEADER];
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    const lesson = cell(row, lessonIdx);
    const chapterRaw = cell(row, chapterIdx);
    const topic = cell(row, topicIdx);
    const notes = cell(row, notesIdx);
    const ach = cell(row, achIdx);
    let cross = cell(row, crossIdx);
    if (blankish(lesson) && blankish(topic)) {
      if (!blankish(chapterRaw)) pendingChapter = formatChapter(chapterRaw) || pendingChapter;
      continue;
    }
    if (blankish(cross)) cross = '-';
    let chapterOut = '-';
    if (!blankish(chapterRaw)) {
      const formatted = formatChapter(chapterRaw);
      if (!sameChapter(formatted, lastChapter)) {
        chapterOut = formatted;
        lastChapter = formatted;
      }
    } else if (!lastChapter && pendingChapter) {
      chapterOut = pendingChapter;
      lastChapter = pendingChapter;
    }
    out.push([lesson, chapterOut, ach, topic, notes, cross]);
  }
  return out.map((cells) => cells.join('\t')).join('\n');
}

function statusLabel(status) {
  const map = {
    uploaded: 'Ready',
    generating: 'Generating…',
    pending_review: 'Done',
    error: 'Error',
    final: 'Done',
  };
  return map[status] || status || '—';
}

/** Format seconds as "X min YY sec". */
function formatMinSec(totalSec) {
  const sec = Math.max(0, Math.floor(Number(totalSec) || 0));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m} min ${String(s).padStart(2, '0')} sec`;
}

export default function PlansPreparationsTab() {
  const { currentUser } = useAuth();
  const canDeleteChapter = currentUser?.role === 'administrator' || currentUser?.role === 'examiner';

  const savedPrefs = useRef(readPpPrefs()).current;
  const [grade, setGrade] = useState(() => {
    const g = parseInt(savedPrefs.grade, 10);
    return Number.isFinite(g) && g >= 1 && g <= 12 ? g : 10;
  });
  const [classes, setClasses] = useState([]);
  const [className, setClassName] = useState(() => String(savedPrefs.className || ''));
  const [subjects, setSubjects] = useState([]);
  const [subject, setSubject] = useState(() => String(savedPrefs.subject || ''));
  const [courseId, setCourseId] = useState(null);

  const [chapters, setChapters] = useState([]);
  const [printChapterIds, setPrintChapterIds] = useState([]); // chapters to merge into one Notes PDF
  const [printBusy, setPrintBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(() => {
    const id = parseInt(savedPrefs.selectedId, 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  });
  const selected = chapters.find((c) => c.id === selectedId) || null;
  const [expandedChapterId, setExpandedChapterId] = useState(() => {
    const id = parseInt(savedPrefs.selectedId, 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  });

  const selectAndToggleChapter = useCallback((chId) => {
    if (selectedId === chId) {
      setExpandedChapterId((prev) => (prev === chId ? null : chId));
      return;
    }
    setSelectedId(chId);
    setExpandedChapterId(chId);
  }, [selectedId]);

  const [title, setTitle] = useState('');
  const [chapterNo, setChapterNo] = useState('');
  const [targetDays, setTargetDays] = useState('');
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [generating, setGenerating] = useState(false);
  const [queued, setQueued] = useState(false);
  const [genStep, setGenStep] = useState('');
  const [runTiming, setRunTiming] = useState(null); // { total_elapsed_sec, total_elapsed_label, started_at, finished_at, … }
  const [queueSnapshot, setQueueSnapshot] = useState(null); // { running, queued, maxConcurrent, … }
  const [myQueuePos, setMyQueuePos] = useState(null); // positionForChapter payload
  const queuePollRef = useRef(null);
  const [days, setDays] = useState([]);
  const [chapterStructure, setChapterStructure] = useState(null);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [outputs, setOutputs] = useState({
    plan: false, notes: false, worksheet: false, mcq: false, tests: [], dayWorksheets: [],
  });
  const [previewViewMode, setPreviewViewMode] = useState('auto'); // auto | text | pdf
  const [expandedTopicDays, setExpandedTopicDays] = useState(() => new Set());
  const [previewKey, setPreviewKey] = useState(null); // 'plan' | 'full_notes' | 'full_worksheet' | 'full_mcq' | 'day:N:step'
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState('');
  const [previewMode, setPreviewMode] = useState('markdown'); // pages | pdf | shots | markdown | ocr | qa
  const [chapterOcrText, setChapterOcrText] = useState('');
  // QA ledger panel: issues + per-question approval state for one day/kind.
  const [qaPanel, setQaPanel] = useState(null); // { day, kind, issues[], blocks[], summary }
  const [qaLoading, setQaLoading] = useState(false);
  const [qaBusy, setQaBusy] = useState(null); // issue id / block id while a mutation runs
  const [qaSummary, setQaSummary] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewShots, setPreviewShots] = useState([]); // [{ file, label, url }]
  const [packPreview, setPackPreview] = useState(null); // { chapterId, ...manifest }
  const [previewBusyHint, setPreviewBusyHint] = useState('Loading preview…');
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  const previewPaneRef = useRef(null);
  const fullNotesCopyRef = useRef(null);
  const previewKeyRef = useRef(null);
  const previewTitleRef = useRef('');
  useEffect(() => { previewKeyRef.current = previewKey; }, [previewKey]);
  useEffect(() => { previewTitleRef.current = previewTitle; }, [previewTitle]);
  const [planSearch, setPlanSearch] = useState('');
  /** What Generate runs: chapter | plan | day | day_notes | day_mcq | all_notes | all_mcq */
  const [genMode, setGenMode] = useState('chapter');
  const [genDayNo, setGenDayNo] = useState('');
  const [chapterPages, setChapterPages] = useState([]); // [{ page, blobUrl }]
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pagesBusy, setPagesBusy] = useState(false);
  const [pagesDownloading, setPagesDownloading] = useState(false);
  const [reocrBusy, setReocrBusy] = useState(false);
  const [divideBusy, setDivideBusy] = useState(false);
  const [reocrPagesInput, setReocrPagesInput] = useState('');
  const [reocrPageBusy, setReocrPageBusy] = useState(null); // page number while single-page OCR runs
  const [reocrScope, setReocrScope] = useState(null); // 'full' | 'pages' | null
  const [ocrEditing, setOcrEditing] = useState(false);
  const [ocrDraft, setOcrDraft] = useState('');
  const [ocrSaving, setOcrSaving] = useState(false);
  const [pageLightbox, setPageLightbox] = useState(null); // 1-based page number or null
  const addPagesRef = useRef(null);
  const packBlobUrlsRef = useRef({});
  const packPreviewRef = useRef(null);
  const [capabilities, setCapabilities] = useState({
    generate: true,
    upload: true,
    message: null,
    providers: { cursor: true, local: true, claude: false, default: 'cursor' },
  });
  const generateAllowed = capabilities.generate !== false;
  const [ocrProvider, setOcrProvider] = useState(readStoredOcrProvider);
  const [ocrLocalModel, setOcrLocalModel] = useState(readStoredOcrLocalModel);
  const [ocrLocalHost, setOcrLocalHost] = useState(readStoredOcrLocalHost);
  const [notesProvider, setNotesProvider] = useState(readStoredNotesProvider);
  const [notesLocalModel, setNotesLocalModel] = useState(readStoredNotesLocalModel);
  const [notesLocalHost, setNotesLocalHost] = useState(readStoredNotesLocalHost);
  const localHosts = Array.isArray(capabilities.providers?.localHosts)
    ? capabilities.providers.localHosts
    : [];

  const ocrOpts = useCallback(() => {
    const o = { provider: ocrProvider };
    if (ocrProvider === 'local' && ocrLocalModel) o.local_model = ocrLocalModel;
    if (ocrProvider === 'local' && ocrLocalHost) o.local_host = ocrLocalHost;
    return o;
  }, [ocrProvider, ocrLocalModel, ocrLocalHost]);

  const notesOpts = useCallback(() => {
    const o = { provider: notesProvider };
    if (notesProvider === 'local' && notesLocalModel) o.local_model = notesLocalModel;
    if (notesProvider === 'local' && notesLocalHost) o.local_host = notesLocalHost;
    return o;
  }, [notesProvider, notesLocalModel, notesLocalHost]);

  const pollRef = useRef(null);

  const pickValidHost = useCallback((prev, hosts, defaultKey) => {
    const keys = new Set(hosts.map((h) => h.key));
    if (prev && keys.has(prev)) return prev;
    if (defaultKey && keys.has(defaultKey)) return defaultKey;
    if (keys.has('mac')) return 'mac';
    if (keys.has('gpu')) return 'gpu';
    return hosts[0]?.key || prev || 'mac';
  }, []);

  useEffect(() => {
    let cancelled = false;
    plansPrepApi.getCapabilities()
      .then((caps) => {
        if (cancelled || !caps) return;
        setCapabilities({
          generate: caps.generate !== false,
          upload: caps.upload !== false,
          message: caps.message || null,
          providers: caps.providers || { cursor: true, local: true, claude: false, default: 'cursor' },
        });
        const p = caps.providers || {};
        const fixProvider = (prev) => {
          if (prev === 'cursor' && p.cursor === false && p.local) return 'local';
          if (prev === 'claude' && p.claude === false) return p.cursor !== false ? 'cursor' : 'local';
          return prev;
        };
        setOcrProvider(fixProvider);
        setNotesProvider(fixProvider);
        const hosts = Array.isArray(p.localHosts) ? p.localHosts : [];
        if (hosts.length) {
          const d = p.defaultLocalHost;
          setOcrLocalHost((prev) => pickValidHost(prev, hosts, d));
          setNotesLocalHost((prev) => pickValidHost(prev, hosts, d));
        }
      })
      .catch(() => { /* older backends — keep generate enabled */ });
    return () => { cancelled = true; };
  }, [pickValidHost]);

  useEffect(() => {
    try { localStorage.setItem(PP_OCR_PROVIDER_KEY, ocrProvider); } catch (_) { /* ignore */ }
  }, [ocrProvider]);
  useEffect(() => {
    try { localStorage.setItem(PP_OCR_LOCAL_MODEL_KEY, ocrLocalModel); } catch (_) { /* ignore */ }
  }, [ocrLocalModel]);
  useEffect(() => {
    try { localStorage.setItem(PP_OCR_LOCAL_HOST_KEY, ocrLocalHost); } catch (_) { /* ignore */ }
  }, [ocrLocalHost]);
  useEffect(() => {
    try {
      localStorage.setItem(PP_NOTES_PROVIDER_KEY, notesProvider);
      localStorage.setItem(PP_PROVIDER_KEY, notesProvider); // legacy
    } catch (_) { /* ignore */ }
  }, [notesProvider]);
  useEffect(() => {
    try {
      localStorage.setItem(PP_NOTES_LOCAL_MODEL_KEY, notesLocalModel);
      localStorage.setItem(PP_LOCAL_MODEL_KEY, notesLocalModel);
    } catch (_) { /* ignore */ }
  }, [notesLocalModel]);
  useEffect(() => {
    try {
      localStorage.setItem(PP_NOTES_LOCAL_HOST_KEY, notesLocalHost);
      localStorage.setItem(PP_LOCAL_HOST_KEY, notesLocalHost);
    } catch (_) { /* ignore */ }
  }, [notesLocalHost]);

  const refreshQueue = useCallback(async () => {
    try {
      const snap = await plansPrepApi.getQueue();
      setQueueSnapshot(snap);
      return snap;
    } catch (_) {
      return null;
    }
  }, []);

  const stopQueuePoll = useCallback(() => {
    if (queuePollRef.current) {
      clearInterval(queuePollRef.current);
      queuePollRef.current = null;
    }
  }, []);

  const startQueuePoll = useCallback(() => {
    stopQueuePoll();
    refreshQueue();
    queuePollRef.current = setInterval(async () => {
      const snap = await refreshQueue();
      const active = (snap?.runningCount || 0) + (snap?.queuedCount || 0);
      if (!active) stopQueuePoll();
    }, 2000);
  }, [refreshQueue, stopQueuePoll]);

  useEffect(() => () => stopQueuePoll(), [stopQueuePoll]);

  useEffect(() => {
    refreshQueue();
  }, [refreshQueue]);

  const revokePackBlobUrls = useCallback(() => {
    Object.values(packBlobUrlsRef.current).forEach((u) => {
      try { URL.revokeObjectURL(u); } catch (_) { /* ignore */ }
    });
    packBlobUrlsRef.current = {};
  }, []);

  const rememberPackPreview = useCallback((manifest) => {
    packPreviewRef.current = manifest;
    setPackPreview(manifest);
    try {
      if (manifest?.ready && manifest?.chapterId) {
        sessionStorage.setItem(
          `plansPrep.packPreview.${manifest.chapterId}`,
          JSON.stringify({
            chapterId: manifest.chapterId,
            ready: true,
            kind: manifest.kind,
            hash: manifest.hash,
            sourceHash: manifest.sourceHash || null,
            builtAt: manifest.builtAt || null,
            pdfs: manifest.pdfs || [],
            images: manifest.images || [],
            pdfByKey: manifest.pdfByKey || {},
          }),
        );
      }
    } catch (_) { /* private mode / quota */ }
  }, []);

  const clearPackPreview = useCallback(() => {
    const id = packPreviewRef.current?.chapterId;
    packPreviewRef.current = null;
    setPackPreview(null);
    revokePackBlobUrls();
    try {
      if (id) sessionStorage.removeItem(`plansPrep.packPreview.${id}`);
    } catch (_) { /* ignore */ }
  }, [revokePackBlobUrls]);

  const getPackFileUrl = useCallback(async (chapterId, filename, contentHash = '') => {
    const key = `${chapterId}::${filename}::${contentHash || 'nohash'}`;
    if (packBlobUrlsRef.current[key]) return packBlobUrlsRef.current[key];
    // Drop older blobs for the same file when pack hash changes.
    Object.keys(packBlobUrlsRef.current).forEach((k) => {
      if (k.startsWith(`${chapterId}::${filename}::`) && k !== key) {
        try { URL.revokeObjectURL(packBlobUrlsRef.current[k]); } catch (_) { /* ignore */ }
        delete packBlobUrlsRef.current[k];
      }
    });
    const blob = await plansPrepApi.fetchPackPreviewFileBlob(chapterId, filename);
    const url = URL.createObjectURL(blob);
    packBlobUrlsRef.current[key] = url;
    return url;
  }, []);

  const revokePageBlobs = useCallback((pages) => {
    (pages || []).forEach((p) => {
      if (p?.blobUrl) URL.revokeObjectURL(p.blobUrl);
    });
  }, []);

  const loadChapterPages = useCallback(async (chapterId) => {
    if (!chapterId) {
      setChapterPages((prev) => {
        revokePageBlobs(prev);
        return [];
      });
      return;
    }
    setPagesLoading(true);
    try {
      const meta = await plansPrepApi.listChapterPages(chapterId);
      const list = meta.pages || [];
      const withBlobs = [];
      for (const p of list) {
        try {
          const blob = await plansPrepApi.fetchChapterPageBlob(chapterId, p.page);
          withBlobs.push({
            page: p.page,
            blob,
            blobUrl: URL.createObjectURL(blob),
          });
        } catch (_) {
          withBlobs.push({ page: p.page, blob: null, blobUrl: null, error: true });
        }
      }
      setChapterPages((prev) => {
        revokePageBlobs(prev);
        return withBlobs;
      });
    } catch (e) {
      setChapterPages((prev) => {
        revokePageBlobs(prev);
        return [];
      });
      // Soft-fail — chapter may have no PDF yet
      if (e.message && !/not found|no pdf/i.test(e.message)) setError(e.message);
    } finally {
      setPagesLoading(false);
    }
  }, [revokePageBlobs]);

  const loadClasses = useCallback(async (g) => {
    try {
      const res = await plansPrepApi.browseClasses(g);
      setClasses(res.classes || []);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  const loadSubjects = useCallback(async (cls) => {
    try {
      const res = await plansPrepApi.browseSubjects(cls);
      setSubjects(res.subjects || []);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  const ensureCourse = useCallback(async (cls, subj) => {
    const res = await plansPrepApi.addCourse(cls, subj);
    setCourseId(res.course_id);
    return res.course_id;
  }, []);

  const loadChapters = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const res = await plansPrepApi.listChapters(cid);
      const list = res.chapters || [];
      setChapters(list);
      setPrintChapterIds((prev) => prev.filter((id) => list.some((c) => c.id === id)));
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    setPrintChapterIds([]);
  }, [courseId]);

  const refreshChapterOcr = useCallback(async (chapterId) => {
    if (!chapterId) {
      setChapterOcrText('');
      return '';
    }
    try {
      const res = await plansPrepApi.getChapter(chapterId);
      const text = String(res.chapter?.extracted_text || '').trim();
      setChapterOcrText(text);
      return text;
    } catch (_) {
      setChapterOcrText('');
      return '';
    }
  }, []);

  /**
   * Open the QA panel for one day/kind: the issue ledger plus every question
   * with its approval state. Read-only — no model calls.
   */
  const loadQaPanel = useCallback(async (chapterId, day, kind = 'notes') => {
    if (!chapterId) return;
    setPreviewKey(`day:${day == null ? 'chapter' : day}:qa_${kind}`);
    setPreviewTitle(`${day == null ? 'Chapter' : `${day}.`} QA — ${kind === 'mcq' ? 'MCQs' : 'Notes'}`);
    setPreviewMode('qa');
    setPreviewViewMode('text');
    setPreviewText('');
    setPreviewErr('');
    setPreviewPdfUrl(null);
    setPreviewShots([]);
    setQaLoading(true);
    try {
      const [issuesRes, blocksRes] = await Promise.all([
        plansPrepApi.getQaIssues(chapterId, { day, kind }),
        plansPrepApi.getQaBlocks(chapterId, { day, kind }),
      ]);
      setQaPanel({
        day,
        kind,
        issues: issuesRes.issues || [],
        blocks: blocksRes.blocks || [],
        summary: issuesRes.summary || null,
      });
      if (issuesRes.summary) setQaSummary(issuesRes.summary);
    } catch (e) {
      setQaPanel({ day, kind, issues: [], blocks: [] });
      setPreviewErr(e.message || 'Could not load QA issues.');
    } finally {
      setQaLoading(false);
    }
  }, []);

  const handleQaIssueStatus = useCallback(async (issue, status) => {
    if (!selectedId || !issue?.id) return;
    setQaBusy(`issue:${issue.id}`);
    setError('');
    try {
      await plansPrepApi.setQaIssueStatus(selectedId, issue.id, status);
      await loadQaPanel(selectedId, qaPanel?.day, qaPanel?.kind || 'notes');
      setMessage(
        status === 'wontfix'
          ? 'Marked as keep-as-is — it will not be reported again.'
          : status === 'accepted'
            ? 'Marked as checked and correct.'
            : 'Issue reopened.',
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setQaBusy(null);
    }
  }, [selectedId, qaPanel, loadQaPanel]);

  /** Freeze a verified answer: later generations restore this exact text. */
  const handleApproveBlock = useCallback(async (block) => {
    if (!selectedId || !block?.id) return;
    setQaBusy(`block:${block.id}`);
    setError('');
    try {
      if (block.approved) {
        await plansPrepApi.revokeQaApproval(selectedId, block.fingerprint, {
          day: qaPanel?.day, kind: qaPanel?.kind || 'notes',
        });
        setMessage('Lock removed — this answer can change on the next generation.');
      } else {
        await plansPrepApi.approveQaBlock(selectedId, {
          day: qaPanel?.day, kind: qaPanel?.kind || 'notes', blockId: block.id,
        });
        setMessage('Answer locked. Future generations will restore this exact text.');
      }
      await loadQaPanel(selectedId, qaPanel?.day, qaPanel?.kind || 'notes');
    } catch (e) {
      setError(e.message);
    } finally {
      setQaBusy(null);
    }
  }, [selectedId, qaPanel, loadQaPanel]);

  /** Re-run the deterministic rules on saved text. Free — no model calls. */
  const handleQaRecheck = useCallback(async (apply) => {
    if (!selectedId) return;
    const day = qaPanel?.day;
    const kind = qaPanel?.kind || 'notes';
    setQaBusy('recheck');
    setError('');
    setMessage(apply ? 'Re-checking and applying format fixes…' : 'Re-checking against the book…');
    try {
      const res = await plansPrepApi.recheckQa(selectedId, { day, kind, apply });
      await loadQaPanel(selectedId, day, kind);
      const bits = [];
      if (res.applied?.length) bits.push(`fixed: ${res.applied.join(', ')}`);
      bits.push(`${(res.issues || []).length} issue(s)`);
      if (!res.grounded) bits.push('no book text available — grounding checks skipped');
      setMessage(`Re-check done — ${bits.join(' · ')}.`);
    } catch (e) {
      setError(e.message);
      setMessage('');
    } finally {
      setQaBusy(null);
    }
  }, [selectedId, qaPanel, loadQaPanel]);

  const refreshOutputs = useCallback(async (chapterId) => {
    if (!chapterId) return;
    try {
      const daysRes = await plansPrepApi.getDays(chapterId);
      const d = daysRes.days || [];
      setDays(d);
      setChapterStructure(daysRes.structure || null);
      setPipelineStatus(daysRes.pipelineStatus || null);
      const testDays = d.filter((day) => (day.steps || []).some((s) => s.key === 'test' && s.done));
      const dayWorksheets = d
        .filter((day) => (day.steps || []).some((s) => s.key === 'worksheet' && s.done))
        .map((day) => day.lesson_no);
      let plan = false;
      let notes = !!daysRes.fullNotesDone;
      let worksheet = !!daysRes.fullWorksheetDone;
      let mcq = !!daysRes.fullMcqDone;
      try {
        await plansPrepApi.getOutput(chapterId, 'plan');
        plan = true;
      } catch (_) { /* not ready */ }
      if (!notes) {
        notes = d.some((day) => (day.steps || []).some((s) => s.key === 'notes' && s.done));
      }
      if (!worksheet) {
        worksheet = dayWorksheets.length > 0;
      }
      setOutputs({
        plan,
        notes: !!daysRes.fullNotesDone || notes,
        fullNotes: !!daysRes.fullNotesDone,
        worksheet: !!daysRes.fullWorksheetDone || worksheet,
        fullWorksheet: !!daysRes.fullWorksheetDone,
        mcq: !!daysRes.fullMcqDone,
        fullMcq: !!daysRes.fullMcqDone,
        tests: testDays.map((t) => t.lesson_no),
        dayWorksheets,
        hasStructure: !!daysRes.hasStructure,
      });
      return daysRes;
    } catch (_) {
      return null;
    }
  }, []);

  useEffect(() => {
    loadClasses(grade);
  }, [grade, loadClasses]);

  useEffect(() => {
    if (className) loadSubjects(className);
    else setSubjects([]);
  }, [className, loadSubjects]);

  useEffect(() => {
    if (className && subject) {
      ensureCourse(className, subject)
        .then((cid) => loadChapters(cid))
        .catch((e) => setError(e.message));
    }
  }, [className, subject, ensureCourse, loadChapters]);

  // Persist grade / class / subject / selected chapter across refresh.
  useEffect(() => {
    writePpPrefs({ grade, className, subject, selectedId });
  }, [grade, className, subject, selectedId]);

  // After classes load, drop a stale saved section that no longer exists for this grade.
  useEffect(() => {
    if (!classes.length || !className) return;
    if (!classes.some((c) => c.class === className)) {
      setClassName('');
      setSubject('');
    }
  }, [classes, className]);

  // After subjects load, drop a stale saved subject.
  useEffect(() => {
    if (!subjects.length || !subject) return;
    if (!subjects.some((s) => s.subject === subject)) {
      setSubject('');
    }
  }, [subjects, subject]);

  // After chapters load, keep selectedId only if it belongs to this course.
  useEffect(() => {
    if (selectedId == null) return;
    if (!chapters.length) {
      // Still loading or empty course — clear selection only when we know the list.
      return;
    }
    if (!chapters.some((c) => c.id === selectedId)) {
      setSelectedId(null);
    }
  }, [chapters, selectedId]);

  // Selecting a chapter expands it; clicking the same chapter again collapses (via toggle).
  useEffect(() => {
    if (selectedId == null) setExpandedChapterId(null);
    else setExpandedChapterId(selectedId);
  }, [selectedId]);

  useEffect(() => {
    if (selectedId) {
      setPreviewKey(null);
      setPreviewText('');
      setPreviewTitle('');
      setPreviewErr('');
      setPreviewMode('markdown');
      setPreviewPdfUrl(null);
      setPreviewShots([]);
      clearPackPreview();
      setPlanSearch('');
      setDays([]);
      setChapterStructure(null);
      setPipelineStatus(null);
      setExpandedTopicDays(new Set());
      setOutputs({
        plan: false, notes: false, worksheet: false, mcq: false, tests: [], dayWorksheets: [],
      });
      refreshOutputs(selectedId);
      refreshChapterOcr(selectedId);
      loadChapterPages(selectedId);
      plansPrepApi.getGeneration(selectedId)
        .then((gen) => {
          setRunTiming(gen.timing || null);
          setGenerating(!!gen.generating);
          setQueued(!!gen.queued);
          setMyQueuePos(gen.queue || null);
          setGenStep(gen.run?.current_step || gen.timing?.step_label || gen.timing?.label || '');
          if (gen.generating) {
            startPoll(selectedId);
            startQueuePoll();
          }
        })
        .catch(() => setRunTiming(null));
    } else {
      setDays([]);
      setChapterOcrText('');
      setPreviewKey(null);
      setPreviewText('');
      setPreviewTitle('');
      setPreviewMode('markdown');
      setPreviewPdfUrl(null);
      setPreviewShots([]);
      setOcrEditing(false);
      setOcrDraft('');
      clearPackPreview();
      setRunTiming(null);
      setOutputs({
        plan: false, notes: false, worksheet: false, mcq: false, tests: [], dayWorksheets: [],
      });
      loadChapterPages(null);
    }
  }, [selectedId, refreshOutputs, refreshChapterOcr, loadChapterPages, clearPackPreview]);

  useEffect(() => () => {
    revokePackBlobUrls();
  }, [revokePackBlobUrls]);

  useEffect(() => () => {
    revokePageBlobs(chapterPages);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- revoke on unmount only
  }, []);

  useEffect(() => {
    setPageLightbox(null);
  }, [selectedId]);

  // QA panel is per-chapter — never show one chapter's issues under another.
  useEffect(() => {
    setQaPanel(null);
    setQaSummary(null);
    if (!selectedId) return;
    plansPrepApi.getQaSummary(selectedId)
      .then((res) => setQaSummary(res.summary || null))
      .catch(() => { /* older backends have no QA ledger */ });
  }, [selectedId]);

  useEffect(() => {
    if (pageLightbox == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setPageLightbox(null);
        return;
      }
      if (!chapterPages.length) return;
      const idx = chapterPages.findIndex((p) => p.page === pageLightbox);
      if (idx < 0) return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = chapterPages[idx - 1] || chapterPages[chapterPages.length - 1];
        if (prev) setPageLightbox(prev.page);
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        const next = chapterPages[idx + 1] || chapterPages[0];
        if (next) setPageLightbox(next.page);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pageLightbox, chapterPages]);

  const handleDeletePage = async (pageNo) => {
    if (!selectedId || pagesBusy) return;
    if (!window.confirm(`Remove page ${pageNo} from this chapter upload?`)) return;
    setPagesBusy(true);
    setError('');
    try {
      await plansPrepApi.deleteChapterPage(selectedId, pageNo);
      setMessage(`Page ${pageNo} removed. Re-extract / regenerate if you already generated.`);
      await loadChapterPages(selectedId);
      if (courseId) await loadChapters(courseId);
    } catch (e) {
      setError(e.message);
    } finally {
      setPagesBusy(false);
    }
  };

  const handleAddPages = async (fileList) => {
    if (!selectedId || !fileList?.length || pagesBusy) return;
    setPagesBusy(true);
    setError('');
    try {
      const res = await plansPrepApi.appendChapterPages(selectedId, fileList);
      setMessage(res.message || `Now ${res.page_count} page(s). Re-extract before generating if needed.`);
      await loadChapterPages(selectedId);
      if (courseId) await loadChapters(courseId);
    } catch (e) {
      setError(e.message);
    } finally {
      setPagesBusy(false);
      if (addPagesRef.current) addPagesRef.current.value = '';
    }
  };

  const pageImageExt = (blob) => {
    const t = String(blob?.type || '').toLowerCase();
    if (t.includes('jpeg') || t.includes('jpg')) return '.jpg';
    if (t.includes('webp')) return '.webp';
    if (t.includes('gif')) return '.gif';
    return '.png';
  };

  const handleDownloadAllPages = async () => {
    const pages = (chapterPages || []).filter((p) => p?.blob || p?.blobUrl);
    if (!pages.length || pagesBusy || pagesDownloading) return;
    setPagesDownloading(true);
    setError('');
    setMessage('');
    try {
      // Resolve blobs (prefer cached; re-fetch if needed)
      const ready = [];
      for (const p of pages) {
        let blob = p.blob || null;
        if (!blob && p.blobUrl) {
          try {
            blob = await fetch(p.blobUrl).then((r) => r.blob());
          } catch (_) { /* fall through */ }
        }
        if (!blob && selectedId) {
          blob = await plansPrepApi.fetchChapterPageBlob(selectedId, p.page);
        }
        if (!blob) continue;
        ready.push({ page: p.page, blob, name: `p${p.page}${pageImageExt(blob)}` });
      }
      if (!ready.length) throw new Error('No page images available to download.');

      // Prefer folder picker (Chrome / Edge / modern Chromium) so the user chooses the destination.
      if (typeof window.showDirectoryPicker === 'function') {
        let dirHandle;
        try {
          dirHandle = await window.showDirectoryPicker({
            id: 'plans-prep-uploaded-pages',
            mode: 'readwrite',
            startIn: 'downloads',
          });
        } catch (pickErr) {
          if (pickErr && (pickErr.name === 'AbortError' || pickErr.name === 'NotAllowedError')) {
            setMessage('Download cancelled.');
            return;
          }
          throw pickErr;
        }
        for (const item of ready) {
          const fileHandle = await dirHandle.getFileHandle(item.name, { create: true });
          const writable = await fileHandle.createWritable();
          await writable.write(item.blob);
          await writable.close();
        }
        setMessage(`Saved ${ready.length} image(s) as p1, p2, … in the folder you chose.`);
        return;
      }

      // Fallback (Safari / Firefox): sequential downloads to the browser Downloads folder.
      for (const item of ready) {
        downloadBlob(item.blob, item.name);
        // Stagger so the browser does not coalesce / block bursts
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 120));
      }
      setMessage(
        `Downloaded ${ready.length} image(s) as p1, p2, …. `
        + 'This browser cannot pick a folder — files went to your Downloads. Use Chrome or Edge to choose a folder.',
      );
    } catch (e) {
      if (e && e.name === 'AbortError') {
        setMessage('Download cancelled.');
      } else {
        setError(e.message || 'Could not download pages.');
      }
    } finally {
      setPagesDownloading(false);
    }
  };

  useEffect(() => {
    if (selected?.target_days) setTargetDays(String(selected.target_days));
    if (selected?.chapter_no != null) setChapterNo(String(selected.chapter_no));
    if (selected?.title) setTitle(String(selected.title));
  }, [selectedId, selected?.target_days, selected?.chapter_no, selected?.title]);

  const loadPreview = useCallback(async (chapterId, key, title, step, day = null, opts = {}) => {
    if (!chapterId) return;
    if (key === 'pages' || step === 'pages') {
      setPreviewKey('pages');
      setPreviewTitle(title || 'Uploaded pages');
      setPreviewText('');
      setPreviewErr('');
      setPreviewLoading(false);
      setPreviewMode('pages');
      setPreviewPdfUrl(null);
      setPreviewShots([]);
      return;
    }
    if (key === 'ocr' || step === 'ocr') {
      setPreviewKey('ocr');
      setPreviewTitle(title || 'Chapter OCR');
      setPreviewErr('');
      setPreviewText('');
      setPreviewPdfUrl(null);
      setPreviewShots([]);
      setPreviewMode('ocr');
      setPreviewViewMode('text');
      setOcrEditing(false);
      setOcrDraft('');
      setPreviewBusyHint('Loading chapter OCR…');
      setPreviewLoading(true);
      try {
        const text = await refreshChapterOcr(chapterId);
        setPreviewText(text);
        setOcrDraft(text);
        if (!text) {
          setPreviewErr('No OCR text saved yet. Run Generate (or Plan only) — the server reads the PDF/images first.');
        }
      } catch (e) {
        setPreviewText('');
        setOcrDraft('');
        setPreviewErr(e.message || 'Could not load chapter OCR.');
      } finally {
        setPreviewLoading(false);
      }
      return;
    }
    // Topic-wise OCR / structured text (canonical extract — not a PDF reconstruction).
    if (step === 'topic_ocr' || String(key || '').includes(':topic_ocr')) {
      const dayNum = day != null ? day : parsePreviewSelection(key)?.day;
      setPreviewKey(key || `day:${dayNum}:topic_ocr`);
      setPreviewTitle(title || `Topic ${dayNum} — Text`);
      setPreviewErr('');
      setPreviewText('');
      setPreviewPdfUrl(null);
      setPreviewShots([]);
      setPreviewMode('ocr');
      setPreviewViewMode('text');
      setOcrEditing(false);
      setPreviewBusyHint('Loading topic text…');
      setPreviewLoading(true);
      try {
        const out = await plansPrepApi.getOutput(chapterId, 'topic_ocr', dayNum);
        const text = String(out.text || out.text_content || '').trim();
        setPreviewText(text);
        if (!text) {
          setPreviewErr('No topic text extracted yet. Run OCR / Generate so topics are detected.');
        }
      } catch (e) {
        setPreviewErr(e.message || 'Could not load topic text.');
      } finally {
        setPreviewLoading(false);
      }
      return;
    }
    if (!step) return;

    setPreviewKey(key);
    setPreviewTitle(title);
    setPreviewErr('');
    setPreviewText('');
    setPreviewPdfUrl(null);
    setPreviewShots([]);

    const wantsPack =
      key === 'full_notes'
      || key === 'full_mcq'
      || key === 'full_short'
      || key === 'full_long'
      || key === 'exercise_notes'
      || key === 'question_bank'
      || key === 'qb_notes'
      || key === 'full_worksheet'
      || /^day:\d+/i.test(String(key || ''));

    const loadMarkdownFallback = async () => {
      const out = await plansPrepApi.getOutput(chapterId, step, day);
      setPreviewText(String(out.text || out.text_content || '').trim());
      setPreviewMode('markdown');
      setPreviewViewMode('text');
    };

    try {
      // Notes / day outputs open as Text first. PDF pack builds only when the
      // user clicks the PDF toggle (opts.wantPdf) or Update Preview.
      if (wantsPack && !opts.wantPdf) {
        setPreviewBusyHint('Loading text…');
        setPreviewLoading(true);
        setPreviewViewMode('text');
        await loadMarkdownFallback();
        return;
      }

      if (!wantsPack) {
        setPreviewBusyHint('Loading preview…');
        setPreviewLoading(true);
        await loadMarkdownFallback();
        return;
      }

      setPreviewViewMode('pdf');

      // Session/memory can hold a stale pack hash after server rebuilds the same filename.
      let manifest = packPreviewRef.current?.chapterId === chapterId && packPreviewRef.current?.ready
        ? packPreviewRef.current
        : null;

      if (!manifest?.ready) {
        try {
          const raw = sessionStorage.getItem(`plansPrep.packPreview.${chapterId}`);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.ready && parsed.chapterId === chapterId) {
              manifest = parsed;
              packPreviewRef.current = parsed;
            }
          }
        } catch (_) { /* ignore */ }
      }

      // Generate pack on PDF click when missing, or when Update Preview forces rebuild.
      const mustBuild = !!opts.rebuildPack || !manifest?.ready;
      setPreviewBusyHint(mustBuild ? 'Generating PDF…' : 'Loading PDF…');
      setPreviewLoading(true);
      const fresh = await plansPrepApi.getPackPreview(chapterId, { rebuild: mustBuild });
      if (fresh?.ready) {
        const prevHash = manifest?.hash;
        manifest = { ...fresh, chapterId };
        rememberPackPreview(manifest);
        if (prevHash && prevHash !== manifest.hash) {
          revokePackBlobUrls();
        }
      } else if (!manifest?.ready) {
        manifest = fresh ? { ...fresh, chapterId } : null;
      }

      if (!manifest?.ready) {
        await loadMarkdownFallback();
        setPreviewErr('PDF not ready yet — showing text. Try PDF again after generation finishes.');
        return;
      }

      const packHash = manifest.hash || '';

      if (key === 'full_notes' || key === 'full_mcq' || key === 'full_worksheet'
        || key === 'full_short' || key === 'full_long'
        || key === 'exercise_notes' || key === 'question_bank' || key === 'qb_notes') {
        const pdfKey = key === 'full_notes' ? 'notes'
          : key === 'full_mcq' ? 'mcqs'
            : key === 'full_short' ? 'shorts'
              : key === 'full_long' ? 'longs'
                : key === 'exercise_notes' ? 'exercise_notes'
                  : key === 'question_bank' ? 'question_bank'
                    : key === 'qb_notes' ? 'qb_notes'
                      : 'workbook';
        const pdf = manifest.pdfByKey?.[pdfKey] || (manifest.pdfs || []).find((p) => p.key === pdfKey);
        if (pdf?.name) {
          const url = await getPackFileUrl(chapterId, pdf.name, packHash);
          setPreviewPdfUrl(url);
          setPreviewMode('pdf');
          return;
        }
        if (key === 'full_short' || key === 'full_long'
          || key === 'exercise_notes' || key === 'question_bank' || key === 'qb_notes') {
          const emptyMsg = {
            full_short: 'No short-question sections found in this chapter’s notes yet.',
            full_long: 'No long-question sections found in this chapter’s notes yet.',
            exercise_notes: 'No exercise notes found yet — generate chapter notes first, then rebuild the pack.',
            question_bank: 'No question-bank stems found yet — generate chapter notes first, then rebuild the pack.',
            qb_notes: 'No question bank + exercise notes pack yet — generate chapter notes first, then rebuild the pack.',
          };
          setPreviewText(emptyMsg[key] || 'Pack PDF not ready yet.');
          setPreviewMode('markdown');
          setPreviewViewMode('text');
          return;
        }
        await loadMarkdownFallback();
        return;
      }

      const dayNo = day != null ? Number(day) : parseInt(String(key).split(':')[1], 10);
      const shots = (manifest.images || []).filter((img) => Number(img.day) === Number(dayNo));
      if (shots.length) {
        const withUrls = [];
        for (const img of shots) {
          const file = img.file || img.name;
          const url = await getPackFileUrl(chapterId, file, packHash);
          withUrls.push({
            file,
            label: file.replace(/\.png$/i, ''),
            page: img.page,
            url,
          });
        }
        setPreviewShots(withUrls);
        setPreviewMode('shots');
        return;
      }

      await loadMarkdownFallback();
      setPreviewErr('No print screenshots for this day yet — showing text. Click PDF again after the pack is built.');
    } catch (e) {
      try {
        await loadMarkdownFallback();
        if (wantsPack) {
          setPreviewErr(e.message
            ? `${e.message} Showing text for now.`
            : 'Print preview unavailable — showing text.');
        }
      } catch (e2) {
        setPreviewText('');
        setPreviewMode('markdown');
        setPreviewViewMode('text');
        setPreviewErr(e2.message || e.message || 'Preview not available yet.');
      }
    } finally {
      setPreviewLoading(false);
    }
  }, [getPackFileUrl, rememberPackPreview, revokePackBlobUrls, refreshChapterOcr]);
  // Auto-open uploaded pages (or plan / first day) when chapter loads.
  useEffect(() => {
    if (!selectedId || previewKey) return;
    loadPreview(selectedId, 'pages', 'Uploaded pages', 'pages', null);
  }, [selectedId, previewKey, loadPreview]);

  useEffect(() => {
    const syncFs = () => {
      const el = previewPaneRef.current;
      setPreviewFullscreen(!!document.fullscreenElement && document.fullscreenElement === el);
    };
    document.addEventListener('fullscreenchange', syncFs);
    return () => document.removeEventListener('fullscreenchange', syncFs);
  }, []);

  const togglePreviewFullscreen = useCallback(async () => {
    const el = previewPaneRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement === el) {
        await document.exitFullscreen();
      } else if (document.fullscreenElement) {
        await document.exitFullscreen();
        await el.requestFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch (err) {
      console.error('[PlansPrep] fullscreen', err);
      setError('Fullscreen is not available in this browser.');
    }
  }, []);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const onFiles = (list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    setFiles(arr);
    if (!title && arr.length === 1) {
      const base = arr[0].name.replace(/\.[^.]+$/, '');
      setTitle(base);
    }
  };

  const waitForGenerationEnd = async (chapterId, maxMs = 180000) => {
    const start = Date.now();
    while (Date.now() - start < maxMs) {
      const gen = await plansPrepApi.getGeneration(chapterId);
      if (!gen.generating) return gen;
      await new Promise((r) => setTimeout(r, 2000));
    }
    throw new Error('Assembly timed out — try Generate pack again.');
  };

  const finalizePack = async (chapterId) => {
    // Whole-chapter / day runs already call rebuildFullNotes|Mcq|Worksheet on the
    // backend. Only assemble here when a printable file is still missing — never
    // force a second generate() (that re-locks the chapter and causes
    // "This chapter is already generating." if the user clicks Generate again).
    let daysRes = await refreshOutputs(chapterId);
    if (!daysRes) return;

    const hasDayNotes = (daysRes.days || []).some((d) => d.steps?.some((s) => s.key === 'notes' && s.done));
    const hasDayWorksheets = (daysRes.days || []).some((d) => d.steps?.some((s) => s.key === 'worksheet' && s.done));
    const hasDayMcqs = (daysRes.days || []).some((d) => d.steps?.some((s) => s.key === 'mcq' && s.done));

    if (hasDayNotes && !daysRes.fullNotesDone) {
      setMessage('Assembling full chapter notes…');
      await plansPrepApi.generate(chapterId, { step: 'full_notes', ...notesOpts() });
      await waitForGenerationEnd(chapterId);
      daysRes = await refreshOutputs(chapterId);
    }
    if (hasDayMcqs && daysRes && !daysRes.fullMcqDone) {
      setMessage('Assembling chapter MCQs…');
      await plansPrepApi.generate(chapterId, { step: 'full_mcq', ...notesOpts() });
      await waitForGenerationEnd(chapterId);
      daysRes = await refreshOutputs(chapterId);
    }
    if (hasDayWorksheets && daysRes && !daysRes.fullWorksheetDone) {
      setMessage('Assembling chapter workbook…');
      await plansPrepApi.generate(chapterId, { step: 'full_worksheet', ...notesOpts() });
      await waitForGenerationEnd(chapterId);
      await refreshOutputs(chapterId);
    }
  };

  const startPoll = (chapterId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    startQueuePoll();
    pollRef.current = setInterval(async () => {
      try {
        const gen = await plansPrepApi.getGeneration(chapterId);
        setGenerating(!!gen.generating);
        setQueued(!!gen.queued);
        setMyQueuePos(gen.queue || null);
        setGenStep(
          gen.queued
            ? (gen.queue?.label ? `Queued: ${gen.queue.label}` : 'Waiting in queue…')
            : (gen.run?.current_step || gen.timing?.step_label || gen.timing?.label || ''),
        );
        setRunTiming(gen.timing || null);
        await refreshOutputs(chapterId);
        if (!gen.generating) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setQueued(false);
          setMyQueuePos(null);
          refreshQueue();
          if (gen.run?.status === 'error') {
            setError(gen.run.error_message || 'Generation failed.');
            setLoading(false);
            setGenerating(false);
            return;
          }
          try {
            await finalizePack(chapterId);
          } catch (e) {
            setError(e.message);
          }
          // Content changed — drop cached print preview so next open rebuilds (still no AI).
          clearPackPreview();
          const ch = await plansPrepApi.getChapter(chapterId);
          setChapterOcrText(String(ch.chapter?.extracted_text || '').trim());
          setChapters((prev) => prev.map((c) => (c.id === chapterId ? { ...c, ...ch.chapter } : c)));
          try {
            const ver = await plansPrepApi.listNoteVersions(chapterId);
            setChapters((prev) => prev.map((c) => (
              c.id === chapterId ? { ...c, note_versions: ver.versions || [] } : c
            )));
          } catch (_) { /* older backends */ }
          await refreshOutputs(chapterId);
          // Reload the open preview so Text/PDF show the new notes, not the
          // previous version that was still painted in the pane.
          const openKey = previewKeyRef.current;
          if (openKey && openKey !== 'pages' && openKey !== 'ocr') {
            try {
              setMessage('Refreshing print preview…');
              const sel = parsePreviewSelection(openKey);
              const step = sel?.step
                || (openKey === 'plan' ? 'plan'
                  : openKey === 'full_notes' || openKey === 'full_short'
                    || openKey === 'exercise_notes' || openKey === 'question_bank'
                    || openKey === 'qb_notes' ? 'full_notes'
                    : openKey === 'full_mcq' ? 'full_mcq'
                      : openKey === 'full_worksheet' ? 'full_worksheet'
                        : null);
              if (step) {
                await loadPreview(
                  chapterId,
                  openKey,
                  previewTitleRef.current || 'Preview',
                  step,
                  sel?.day != null ? sel.day : null,
                  // Stay on text after gen; user clicks PDF when they want the pack.
                );
              }
            } catch (_) { /* keep finished message */ }
          }
          setMessage('Generation finished. Download your files below.');
          setLoading(false);
          setGenerating(false);
        }
      } catch (e) {
        /* keep polling */
      }
    }, 2000);
  };

  const handleGenerate = async () => {
    if (!generateAllowed) {
      setError(capabilities.message || 'Generate is disabled on this server. Use the local Mac for notes.');
      return;
    }
    setError('');
    setMessage('');
    if (!className || !subject) {
      setError('Select class and subject first.');
      return;
    }
    if (!files.length && !selectedId) {
      setError('Upload a chapter PDF or screenshots.');
      return;
    }

    const mode = genMode || 'chapter';
    const previewDay = parsePreviewSelection(previewKey)?.day;
    const dayNum = parseInt(genDayNo || previewDay || '', 10);
    const needsDay = mode === 'day' || mode === 'day_notes' || mode === 'day_mcq';
    if (needsDay && !Number.isFinite(dayNum)) {
      setError('Pick a plan day (click it on the left, or enter Day #), then generate.');
      return;
    }
    if ((mode === 'day' || mode === 'day_notes' || mode === 'day_mcq' || mode === 'all_notes' || mode === 'all_mcq' || mode === 'plan')
      && files.length === 0 && selectedId && !outputs.plan && mode !== 'chapter' && mode !== 'plan') {
      // Scoped day work needs a teaching plan first (backend can build it if missing,
      // but warn when user clearly expects an existing plan day).
      if (needsDay && !days.length) {
        setError('Generate the teaching plan first (mode: Plan only or Whole chapter), then run one day.');
        return;
      }
    }

    setLoading(true);
    try {
      let chapterId = selectedId;
      let cid = courseId;
      if (!cid) cid = await ensureCourse(className, subject);

      if (files.length) {
        const up = await plansPrepApi.uploadPack(cid, {
          files,
          title: title.trim() || undefined,
          targetDays: targetDays || undefined,
          chapterNo: chapterNo || undefined,
        });
        chapterId = up.chapter.id;
        setSelectedId(chapterId);
        setFiles([]);
        setTitle('');
        setChapterNo('');
        await loadChapters(cid);
      } else if (selectedId) {
        const patch = {
          title: (title.trim() || selected.title || 'Chapter').trim(),
        };
        if (targetDays) patch.target_days = parseInt(targetDays, 10);
        if (chapterNo) patch.chapter_no = parseInt(chapterNo, 10);
        await plansPrepApi.updateChapter(selectedId, patch);
        await loadChapters(cid);
      }

      const chRes = await plansPrepApi.getChapter(chapterId);
      const ch = chRes.chapter;
      if (ch.extraction_flag === 'thin' || !ch.has_text) {
        setMessage('Reading chapter from PDF/images…');
        await plansPrepApi.extractText(chapterId, ocrOpts());
        await refreshChapterOcr(chapterId);
      }

      const genOpts = {};
      let msg = 'Generating teaching plan, notes, tests, and answer keys…';
      if (mode === 'plan') {
        genOpts.step = 'plan';
        msg = 'Generating teaching plan only…';
      } else if (mode === 'day') {
        genOpts.day = dayNum;
        msg = `Generating Day ${dayNum} only (all steps)…`;
      } else if (mode === 'day_notes') {
        genOpts.day = dayNum;
        genOpts.step = 'notes';
        msg = `Generating notes for Day ${dayNum} only…`;
      } else if (mode === 'day_mcq') {
        genOpts.day = dayNum;
        genOpts.step = 'mcq';
        msg = `Generating MCQs for Day ${dayNum} only…`;
      } else if (mode === 'all_notes') {
        genOpts.step = 'notes';
        msg = 'Generating notes for every plan day (no MCQs/worksheets)…';
      } else if (mode === 'all_mcq') {
        genOpts.step = 'mcq';
        msg = 'Generating MCQs for every plan day (notes skipped)…';
      } else {
        msg = 'Generating whole chapter (plan + every day)…';
      }

      setMessage(msg);
      setGenerating(true);
      setQueued(true);
      setRunTiming(null);
      const genRes = await plansPrepApi.generate(chapterId, { ...genOpts, ...notesOpts() });
      if (genRes?.queue) setQueueSnapshot(genRes.queue);
      if (genRes?.position) setMyQueuePos(genRes.position);
      if (genRes?.position?.status === 'queued' && genRes.position.position != null) {
        setMessage(
          `${msg} You’re #${genRes.position.position} in line `
          + `(${genRes.position.runningCount}/${genRes.position.maxConcurrent} running).`,
        );
      } else {
        setQueued(false);
      }
      startPoll(chapterId);
    } catch (e) {
      setError(e.message);
      setLoading(false);
      setGenerating(false);
    }
  };

  /** Regenerate one plan day (all its steps), or only the previewed step. */
  const handleRegenerateDay = async ({ stepOnly = false, stepKey = null } = {}) => {
    if (!generateAllowed) {
      setError(capabilities.message || 'Generate is disabled on this server. Use the local Mac for notes.');
      return;
    }
    setError('');
    setMessage('');
    if (!selectedId) {
      setError('Select a chapter first.');
      return;
    }
    if (!outputs.plan && !days.length) {
      setError('Generate the teaching plan first, then regenerate a single day.');
      return;
    }
    const sel = parsePreviewSelection(previewKey);
    const dayFromInput = parseInt(genDayNo || '', 10);
    const dayNo = sel?.day || (Number.isFinite(dayFromInput) ? dayFromInput : null);
    if (!dayNo) {
      setError('Select a plan day on the left, then click Regenerate day.');
      return;
    }
    const dayMeta = days.find((d) => d.lesson_no === dayNo);
    const availableSteps = (dayMeta?.steps || []).map((s) => s.key);
    let step = null;
    if (stepKey) {
      step = availableSteps.includes(stepKey) ? stepKey : null;
      if (!step) {
        setError(`Day ${dayNo} has no "${stepKey}" step in this plan.`);
        return;
      }
    } else if (stepOnly) {
      step = sel.step && availableSteps.includes(sel.step) ? sel.step : preferredDayStep(dayMeta);
      if (!step) {
        setError(`Day ${dayNo} has no steps to regenerate yet.`);
        return;
      }
    }
    setLoading(true);
    try {
      const chRes = await plansPrepApi.getChapter(selectedId);
      const ch = chRes.chapter;
      if (ch.extraction_flag === 'thin' || !ch.has_text) {
        setMessage('Reading chapter from PDF/images…');
        await plansPrepApi.extractText(selectedId, ocrOpts());
        await refreshChapterOcr(selectedId);
      }
      const label = step
        ? `Day ${dayNo} — ${step}`
        : `Day ${dayNo}${dayMeta?.topic ? ` (${dayMeta.topic})` : ''}`;
      setMessage(`Regenerating ${label} only (saves tokens vs full chapter)…`);
      setGenerating(true);
      setQueued(true);
      setRunTiming(null);
      const genRes = await plansPrepApi.generate(selectedId, {
        day: dayNo,
        ...(step ? { step } : {}),
        ...notesOpts(),
      });
      if (genRes?.queue) setQueueSnapshot(genRes.queue);
      if (genRes?.position) setMyQueuePos(genRes.position);
      if (genRes?.position?.status === 'queued' && genRes.position.position != null) {
        setMessage(
          `Regenerating ${label}… You’re #${genRes.position.position} in line `
          + `(${genRes.position.runningCount}/${genRes.position.maxConcurrent} running).`,
        );
      } else {
        setQueued(false);
      }
      startPoll(selectedId);
    } catch (e) {
      setError(e.message);
      setLoading(false);
      setGenerating(false);
    }
  };

  const handleOcrBook = async () => {
    if (!generateAllowed) {
      setError(capabilities.message || 'OCR is disabled on this server.');
      return;
    }
    if (reocrBusy || generating || loading) return;
    setError('');
    setMessage('');
    if (!className || !subject) {
      setError('Select class and subject first.');
      return;
    }
    if (!files.length && !selectedId) {
      setError('Upload a chapter PDF or screenshots, or select a chapter.');
      return;
    }

    setLoading(true);
    setReocrBusy(true);
    setReocrScope('full');
    try {
      let chapterId = selectedId;
      let cid = courseId;
      if (!cid) cid = await ensureCourse(className, subject);

      if (files.length) {
        setMessage('Uploading chapter…');
        const up = await plansPrepApi.uploadPack(cid, {
          files,
          title: title.trim() || undefined,
          targetDays: targetDays || undefined,
          chapterNo: chapterNo || undefined,
        });
        chapterId = up.chapter.id;
        setSelectedId(chapterId);
        setFiles([]);
        setTitle('');
        setChapterNo('');
        await loadChapters(cid);
      } else if (selectedId) {
        const patch = {
          title: (title.trim() || selected?.title || 'Chapter').trim(),
        };
        if (targetDays) patch.target_days = parseInt(targetDays, 10);
        if (chapterNo) patch.chapter_no = parseInt(chapterNo, 10);
        await plansPrepApi.updateChapter(selectedId, patch);
        await loadChapters(cid);
      }

      const ocrLabel = ocrProvider === 'local'
        ? `local${ocrLocalModel ? ` · ${ocrLocalModel}` : ''}${ocrLocalHost ? ` @ ${ocrLocalHost}` : ''}`
        : ocrProvider === 'claude' ? 'Claude' : 'Cursor';
      setMessage(`OCR book (${ocrLabel})…`);
      const res = await plansPrepApi.extractText(chapterId, ocrOpts());
      const text = await refreshChapterOcr(chapterId);
      const len = res.length || text.length || 0;
      const eqNote = res.hasChemEquations
        ? ' Yellow-box equations were added to Chapter OCR.'
        : '';
      setMessage(`OCR finished (${len} chars).${eqNote}`);
      await loadPreview(chapterId, 'ocr', 'Chapter OCR', 'ocr', null);
    } catch (e) {
      setError(e.message || 'OCR failed.');
      setMessage('');
    } finally {
      setLoading(false);
      setReocrBusy(false);
      setReocrScope(null);
    }
  };

  const handleReOcrBook = async (pagesSpec, { forceFull = false } = {}) => {
    if (!selectedId) {
      setError('Select a chapter first.');
      return;
    }
    if (!generateAllowed) {
      setError(capabilities.message || 'Re-OCR is disabled on this server.');
      return;
    }
    if (reocrBusy || generating || loading) return;

    let pages = null;
    if (!forceFull) {
      if (pagesSpec != null && String(pagesSpec).trim() !== '') {
        pages = String(pagesSpec).trim();
      }
    }

    setError('');
    setReocrScope(pages ? 'pages' : 'full');
    const ocrLabel = ocrProvider === 'local'
      ? `local${ocrLocalModel ? ` · ${ocrLocalModel}` : ''}${ocrLocalHost ? ` @ ${ocrLocalHost}` : ''}`
      : ocrProvider === 'claude' ? 'Claude' : 'Cursor';
    setMessage(
      pages
        ? `Re-OCR pages ${pages} (${ocrLabel})…`
        : `Re-OCR book (${ocrLabel})…`,
    );
    setReocrBusy(true);
    try {
      const res = await plansPrepApi.extractText(selectedId, {
        ...(pages ? { pages } : {}),
        ...ocrOpts(),
      });
      const text = await refreshChapterOcr(selectedId);
      const len = res.length || text.length || 0;
      const pageNote = Array.isArray(res.pages) && res.pages.length
        ? ` Pages ${res.pages.map((p) => `p${p}`).join(', ')} updated.`
        : pages
          ? ` Pages ${pages} updated.`
          : '';
      const eqNote = res.hasChemEquations
        ? ' Yellow-box equations were added to Chapter OCR.'
        : '';
      setMessage(`Re-OCR finished (${len} chars).${pageNote}${eqNote}`);
      await loadPreview(selectedId, 'ocr', 'Chapter OCR', 'ocr', null);
    } catch (e) {
      setError(e.message || 'Re-OCR failed.');
      setMessage('');
    } finally {
      setReocrBusy(false);
      setReocrPageBusy(null);
      setReocrScope(null);
    }
  };

  const handleReOcrPages = async () => {
    const spec = reocrPagesInput.trim();
    if (!spec) {
      setError('Enter page numbers first (e.g. p1, p3 or 1-3).');
      return;
    }
    await handleReOcrBook(spec);
  };

  const handleReOcrPage = async (pageNum) => {
    const n = parseInt(pageNum, 10);
    if (!Number.isFinite(n) || n < 1) return;
    setReocrPageBusy(n);
    await handleReOcrBook(`p${n}`);
  };

  /** Split Chapter OCR into per-topic Text only — inspect before Gen Notes. */
  const handleDivideTopics = async () => {
    if (!selectedId) {
      setError('Select a chapter first.');
      return;
    }
    if (!generateAllowed) {
      setError(capabilities.message || 'Divide topics is disabled on this server.');
      return;
    }
    if (!chapterOcrText || chapterOcrText.trim().length < 80) {
      setError('Run Chapter OCR first, then Divide topics.');
      return;
    }
    setDivideBusy(true);
    setError('');
    setMessage('Dividing chapter OCR into topics…');
    try {
      const res = await plansPrepApi.divideTopics(selectedId, notesOpts());
      await refreshOutputs(selectedId);
      const n = res.topicCount || 0;
      setMessage(
        res.message
        || `Divided into ${n} topic(s). Open each topic → Text to verify, then Gen Notes.`,
      );
      setPipelineStatus({
        stage: 'topics_detected',
        topicCount: n,
        method: res.method,
      });
      // Open first topic Text for inspection
      if (n > 0) {
        const first = (res.topics && res.topics[0]) || { lesson_no: 1, title: 'Topic 1' };
        setExpandedTopicDays(new Set([first.lesson_no]));
        await loadPreview(
          selectedId,
          `day:${first.lesson_no}:topic_ocr`,
          `${first.lesson_no}. ${first.title || 'Topic'} — Text`,
          'topic_ocr',
          first.lesson_no,
        );
      }
    } catch (e) {
      setError(e.message || 'Topic division failed.');
      setMessage('');
    } finally {
      setDivideBusy(false);
    }
  };

  const ocrDirty = ocrEditing && ocrDraft !== previewText;

  const startOcrEdit = () => {
    setOcrDraft(previewText || chapterOcrText || '');
    setOcrEditing(true);
    setError('');
  };

  const cancelOcrEdit = () => {
    setOcrDraft(previewText || '');
    setOcrEditing(false);
  };

  const saveOcrText = async () => {
    if (!selectedId || ocrSaving) return;
    setOcrSaving(true);
    setError('');
    try {
      await plansPrepApi.updateChapterText(selectedId, ocrDraft);
      const text = String(ocrDraft || '');
      setPreviewText(text);
      setChapterOcrText(text.trim());
      setOcrEditing(false);
      setMessage(`Chapter OCR saved (${text.length} chars).`);
    } catch (e) {
      setError(e.message || 'Could not save OCR text.');
    } finally {
      setOcrSaving(false);
    }
  };

  const handleGenerateScoped = async (opts, label) => {
    if (!generateAllowed) {
      setError(capabilities.message || 'Generate is disabled on this server. Use the local Mac for notes.');
      return;
    }
    setError('');
    setMessage('');
    if (!selectedId) {
      setError('Select a chapter first.');
      return;
    }
    if (!outputs.plan && !days.length && opts.step !== 'plan') {
      setError('Generate the teaching plan first.');
      return;
    }
    setLoading(true);
    try {
      const chRes = await plansPrepApi.getChapter(selectedId);
      const ch = chRes.chapter;
      if (ch.extraction_flag === 'thin' || !ch.has_text) {
        setMessage('Reading chapter from PDF/images…');
        await plansPrepApi.extractText(selectedId, ocrOpts());
      }
      setMessage(label);
      setGenerating(true);
      setQueued(true);
      setRunTiming(null);
      const genRes = await plansPrepApi.generate(selectedId, { ...opts, ...notesOpts() });
      if (genRes?.queue) setQueueSnapshot(genRes.queue);
      if (genRes?.position) setMyQueuePos(genRes.position);
      if (genRes?.position?.status === 'queued' && genRes.position.position != null) {
        setMessage(
          `${label} You’re #${genRes.position.position} in line `
          + `(${genRes.position.runningCount}/${genRes.position.maxConcurrent} running).`,
        );
      } else {
        setQueued(false);
      }
      startPoll(selectedId);
    } catch (e) {
      setError(e.message);
      setLoading(false);
      setGenerating(false);
    }
  };

  /** Reassemble printable full-chapter notes from day notes (no AI). Also cleans Computer HTML stubs. */
  const handleRebuildFullNotes = async () => {
    if (!selectedId) {
      setError('Select a chapter first.');
      return;
    }
    const hasDayNotes = (days || []).some((d) => d.steps?.some((s) => s.key === 'notes' && s.done));
    if (!hasDayNotes && !outputs.fullNotes) {
      setError('No day notes yet — use All notes or Generate first.');
      return;
    }
    clearPackPreview();
    await handleGenerateScoped(
      { step: 'full_notes' },
      'Rebuilding full chapter notes from day notes…',
    );
    // Force a fresh pack PDF (disk cache can keep stub PDFs under the same filename).
    setMessage('Refreshing print preview…');
    try {
      await loadPreview(selectedId, 'exercise_notes', 'Exercise notes', 'full_notes', null, {
        wantPdf: true,
        rebuildPack: true,
      });
    } catch (e) {
      setError(e.message || 'Preview refresh failed.');
    }
  };

  const handleStop = async () => {
    if (!selectedId) return;
    try {
      await plansPrepApi.stopGeneration(selectedId);
      setMessage('Stopping…');
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDownloadDocx = async (step, day, label) => {
    try {
      const blob = await plansPrepApi.downloadDocx(selectedId, step, day);
      const safe = (selected?.title || 'chapter').replace(/[^\w.\-]+/g, '_');
      downloadBlob(blob, `${safe} - ${label}.docx`);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDownloadWorkbookPack = async () => {
    try {
      setMessage('Building EduPage workbook (auto-fixes empty pages if needed)…');
      const blob = await plansPrepApi.downloadWorkbookPack(selectedId);
      const safe = (selected?.title || 'chapter').replace(/[^\w.\-]+/g, '_');
      downloadBlob(blob, `${safe} - EduPage Workbook.zip`);
      setMessage('EduPage workbook pack downloaded.');
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDownloadNotesPack = async () => {
    try {
      setMessage('Building EduPage notes pack (PDFs + lecture note images + MCQ txt)…');
      const blob = await plansPrepApi.downloadNotesPack(selectedId);
      const safe = (selected?.title || 'chapter').replace(/[^\w.\-]+/g, '_');
      downloadBlob(blob, `${safe} - EduPage Notes.zip`);
      setMessage('EduPage notes pack downloaded.');
    } catch (e) {
      setError(e.message);
    }
  };

  const togglePrintChapter = (chId, e) => {
    e.stopPropagation();
    setPrintChapterIds((prev) => {
      if (prev.includes(chId)) return prev.filter((id) => id !== chId);
      setError('');
      return [...prev, chId];
    });
  };

  const handleDownloadCombinedPackPdf = async (pack = 'notes') => {
    const packLabel = pack === 'mcqs' ? 'MCQs' : 'Notes';
    if (!courseId) {
      setError('Select class and subject first.');
      return;
    }
    if (printChapterIds.length < 1) {
      setError(`Tick chapters that already have ${packLabel.toLowerCase()}, then download.`);
      return;
    }
    setPrintBusy(true);
    setError('');
    try {
      setMessage(`Building ${packLabel} PDF for ${printChapterIds.length} chapter(s)…`);
      const blob = await plansPrepApi.downloadCombinedNotesPdf(courseId, printChapterIds, pack);
      const nos = chapters
        .filter((c) => printChapterIds.includes(c.id))
        .sort((a, b) => (a.chapter_no || 0) - (b.chapter_no || 0))
        .map((c) => c.chapter_no)
        .join('-');
      const safe = `${className || 'class'} ${subject || 'subject'}`.replace(/[^\w.\- ]+/g, '_').trim();
      downloadBlob(blob, `${safe} - Chapters ${nos} ${packLabel}.pdf`);
      setMessage(`${packLabel} PDF ready (${printChapterIds.length} chapter${printChapterIds.length === 1 ? '' : 's'}).`);
    } catch (e) {
      setError(e.message);
    } finally {
      setPrintBusy(false);
    }
  };

  const copyTextToClipboard = async (text) => {
    const value = String(text ?? '');
    if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(value);
      return;
    }
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    ta.style.top = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, value.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    if (!ok) throw new Error('Clipboard copy is not supported in this browser.');
  };

  const handleCopyPlan = async () => {
    try {
      const out = await plansPrepApi.getOutput(selectedId, 'plan');
      const tsv = planToEdupageTsv(out.text || '', {
        chapterNo: selected?.chapter_no,
        chapterName: selected?.title,
      });
      await copyTextToClipboard(tsv);
      setMessage('Teaching plan copied — paste into EduPage Import from Excel.');
    } catch (e) {
      setError(e.message);
    }
  };

  const handleCopyFullChapterText = async () => {
    if (!selectedId) return;
    let ephemeral = null;
    try {
      setError('');
      // Same rendered notes the Text preview shows, so Word keeps bold and tables.
      const live = previewKey === 'full_notes'
        && previewMode === 'markdown'
        && fullNotesCopyRef.current
        && fullNotesCopyRef.current.childNodes.length
        ? fullNotesCopyRef.current
        : null;
      let node = live;
      if (!node) {
        let text = previewKey === 'full_notes' && String(previewText || '').trim()
          ? String(previewText)
          : '';
        if (!text.trim()) {
          const out = await plansPrepApi.getOutput(selectedId, 'full_notes');
          text = out.text || '';
        }
        if (!String(text).trim()) {
          setError('No full chapter notes text to copy yet. Generate notes first.');
          return;
        }
        ephemeral = renderNotesCopyNode(text);
        node = ephemeral;
      }
      await copyRichHtml(node);
      setMessage('Full chapter notes copied. Paste into Word to keep bold text and tables.');
    } catch (e) {
      setError(e.message || 'Could not copy full chapter text.');
    } finally {
      releaseNotesCopyNode(ephemeral);
    }
  };

  const handleDownloadPlan = async () => {
    try {
      const out = await plansPrepApi.getOutput(selectedId, 'plan');
      const tsv = planToEdupageTsv(out.text || '', {
        chapterNo: selected?.chapter_no,
        chapterName: selected?.title,
      });
      const safe = (selected?.title || 'chapter').replace(/[^\w.\-]+/g, '_');
      downloadText(tsv, `${safe} - Teaching Plan.txt`);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleActivateNoteVersion = async (chapterId, versionId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!chapterId || !versionId) return;
    setError('');
    try {
      const res = await plansPrepApi.activateNoteVersion(chapterId, versionId);
      setChapters((prev) => prev.map((c) => (
        c.id === chapterId ? { ...c, note_versions: res.versions || [] } : c
      )));
      clearPackPreview();
      if (selectedId === chapterId) {
        await refreshOutputs(chapterId);
      }
      setMessage(`Active notes: ${res.version?.label || 'selected version'}.`);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRenameNoteVersion = async (chapterId, version, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!chapterId || !version?.id) return;
    const current = String(version.label || '').trim();
    const next = window.prompt('Rename notes version:', current);
    if (next == null) return;
    const label = String(next).replace(/\s+/g, ' ').trim();
    if (!label) {
      setError('Version name cannot be empty.');
      return;
    }
    if (label === current) return;
    setError('');
    try {
      const res = await plansPrepApi.renameNoteVersion(chapterId, version.id, label);
      setChapters((prev) => prev.map((c) => (
        c.id === chapterId ? { ...c, note_versions: res.versions || [] } : c
      )));
      setMessage(`Renamed to “${res.version?.label || label}”.`);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteNoteVersion = async (chapterId, version, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!chapterId || !version?.id) return;
    const label = version.label || 'this version';
    if (!window.confirm(
      `Delete notes version “${label}”? This removes its generated notes/worksheets for this version only.`,
    )) return;
    setError('');
    try {
      const res = await plansPrepApi.deleteNoteVersion(chapterId, version.id);
      setChapters((prev) => prev.map((c) => (
        c.id === chapterId ? { ...c, note_versions: res.versions || [] } : c
      )));
      clearPackPreview();
      if (selectedId === chapterId) {
        await refreshOutputs(chapterId);
      }
      const activeLabel = res.version?.label;
      setMessage(
        activeLabel
          ? `Deleted “${label}”. Active notes: ${activeLabel}.`
          : `Deleted “${label}”.`,
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteChapter = async (ch, e) => {
    e.stopPropagation();
    if (!canDeleteChapter) return;
    const label = `Ch ${ch.chapter_no}: ${ch.title}`;
    if (!window.confirm(`Delete "${label}"? This removes its PDF and all generated content.`)) return;
    try {
      setError('');
      await plansPrepApi.deleteChapter(ch.id);
      if (selectedId === ch.id) {
        setSelectedId(null);
        setDays([]);
        setOutputs({
          plan: false, notes: false, worksheet: false, mcq: false, tests: [], dayWorksheets: [],
        });
      }
      if (courseId) await loadChapters(courseId);
      setMessage(`Deleted ${label}.`);
    } catch (err) {
      setError(err.message);
    }
  };

  const progressPct = () => {
    if (!days.length) return generating ? 8 : 0;
    const totalSteps = days.reduce((n, d) => n + (d.steps?.length || 0), 0);
    const doneSteps = days.reduce((n, d) => n + (d.steps?.filter((s) => s.done).length || 0), 0);
    const base = totalSteps ? Math.round((doneSteps / totalSteps) * 85) : 10;
    return generating ? Math.min(95, base + 5) : (outputs.plan ? 100 : base);
  };

  const filteredDays = days.filter((d) => {
    const q = planSearch.trim().toLowerCase();
    if (!q) return true;
    return `${d.lesson_no} ${d.topic || ''} ${d.label || ''}`.toLowerCase().includes(q);
  });

  const selectedDayPreview = parsePreviewSelection(previewKey);
  const effectiveGenDay = genDayNo || (selectedDayPreview?.day != null ? String(selectedDayPreview.day) : '');
  const genModeNeedsDay = genMode === 'day' || genMode === 'day_notes' || genMode === 'day_mcq';
  const lightboxPage = pageLightbox != null
    ? (chapterPages.find((p) => p.page === pageLightbox) || null)
    : null;
  const lightboxIdx = lightboxPage
    ? chapterPages.findIndex((p) => p.page === lightboxPage.page)
    : -1;

  return (
    <div className="pp-page">
      <div className="pp-toolbar">
      {/^gk$/i.test(subject) && (
        <p className="pp-head-note">
          GK (Class 1–3): solved workbook — Regenerate Day, or Whole chapter if needed.
        </p>
      )}

      {error && (
        <div className="pp-status error" role="alert">
          <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
        </div>
      )}
      {message && !error && (
        <div className="pp-status">{message}</div>
      )}

      {(queued || myQueuePos?.status === 'queued') && myQueuePos?.position != null && (
        <div className="pp-queue-banner" role="status">
          You’re <strong>#{myQueuePos.position}</strong> in queue
          {myQueuePos.label ? <> — {myQueuePos.label}</> : null}
          {' · '}{myQueuePos.runningCount}/{myQueuePos.maxConcurrent} running
          {myQueuePos.ahead > 0 ? ` · ${myQueuePos.ahead} ahead` : ''}
        </div>
      )}

      {queueSnapshot && ((queueSnapshot.runningCount || 0) + (queueSnapshot.queuedCount || 0) > 0) && (
        <div className="pp-queue-panel">
          <div className="pp-queue-panel-head">
            <span className="pp-queue-title">Queue</span>
            <span className="pp-queue-cap">
              {queueSnapshot.runningCount}/{queueSnapshot.maxConcurrent} running
              {queueSnapshot.queuedCount > 0 ? ` · ${queueSnapshot.queuedCount} waiting` : ''}
            </span>
          </div>
          <ul className="pp-queue-list">
            {(queueSnapshot.running || []).map((j) => (
              <li key={j.id} className="pp-queue-item running" title={j.label || ''}>
                <span className="pp-queue-pos">●</span>
                <span className="pp-queue-who">{j.displayName || j.username}</span>
                <span className="pp-queue-what">
                  {[j.className, j.subject].filter(Boolean).join(' · ')}
                  {j.chapterNo != null ? ` · Ch ${j.chapterNo}` : ''}
                  {j.label ? ` · ${j.label}` : ''}
                </span>
              </li>
            ))}
            {(queueSnapshot.queued || []).map((j) => (
              <li key={j.id} className="pp-queue-item waiting" title={j.label || ''}>
                <span className="pp-queue-pos">#{j.position}</span>
                <span className="pp-queue-who">{j.displayName || j.username}</span>
                <span className="pp-queue-what">
                  {[j.className, j.subject].filter(Boolean).join(' · ')}
                  {j.chapterNo != null ? ` · Ch ${j.chapterNo}` : ''}
                  {j.label ? ` · ${j.label}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="pp-grid">
        <div className="pp-card">
          <h2>1. Class &amp; subject</h2>
          <div className="pp-row">
            <div className="pp-field pp-field-grade">
              <label>Grade</label>
              <select value={grade} onChange={(e) => { setGrade(Number(e.target.value)); setClassName(''); setSubject(''); }}>
                {GRADES.map((g) => <option key={g} value={g}>Class {g}</option>)}
              </select>
            </div>
            <div className="pp-field pp-field-section">
              <label>Section</label>
              <select value={className} onChange={(e) => { setClassName(e.target.value); setSubject(''); }}>
                <option value="">Select…</option>
                {classes.map((c) => (
                  <option key={c.id || c.class} value={c.class}>{c.class}</option>
                ))}
              </select>
            </div>
            <div className="pp-field pp-field-subject">
              <label>Subject</label>
              <select value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!className}>
                <option value="">Select…</option>
                {subjects.map((s) => (
                  <option key={s.id || s.subject} value={s.subject}>{s.subject}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="pp-card pp-card-upload">
          <h2>2. Chapter upload</h2>
          {!generateAllowed && (
            <div className="pp-banner pp-banner-warn" role="status">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              {' '}
              {capabilities.message
                || 'Upload only on this site. Generate notes on the local Mac (fast107.local), not on fastgrader.org.'}
            </div>
          )}
          <div className="pp-upload-line">
            <div className="pp-field pp-field-ch">
              <label>Ch #</label>
              <input
                type="number"
                min={1}
                max={99}
                value={chapterNo}
                onChange={(e) => setChapterNo(e.target.value)}
                placeholder="1"
                title="Printed as Chapter N in notes, workbook, and EduPage plan"
              />
            </div>
            <div className="pp-field pp-field-title">
              <label>Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Chapter title"
              />
            </div>
            <div className="pp-field pp-field-days">
              <label>Days</label>
              <input
                type="number"
                min={1}
                max={30}
                value={targetDays}
                onChange={(e) => setTargetDays(e.target.value)}
                placeholder="Auto"
                title="Leave blank — AI suggests"
              />
            </div>
            <div
              className={`pp-dropzone pp-dropzone-inline${dragOver ? ' drag-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); onFiles(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && fileRef.current?.click()}
              title="Drop PDF, DOCX, or page screenshots — or click to browse"
            >
              <FontAwesomeIcon icon={faUpload} />
              <span className="pp-dropzone-text">
                {files.length
                  ? `${files.length} file${files.length === 1 ? '' : 's'}`
                  : 'PDF / images'}
              </span>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept=".pdf,.docx,.txt,image/png,image/jpeg,image/webp"
                onChange={(e) => onFiles(e.target.files)}
              />
            </div>
            <label className="pp-gen-scope" title="What Gen Notes will run">
              <span className="pp-gen-scope-label">Run</span>
              <select
                value={genMode}
                disabled={loading || generating || !generateAllowed}
                onChange={(e) => setGenMode(e.target.value)}
              >
                <option value="chapter">Whole chapter</option>
                <option value="plan">Plan only</option>
                <option value="day">One day (all steps)</option>
                <option value="day_notes">Notes — one day</option>
                <option value="day_mcq">MCQs — one day</option>
                <option value="all_notes">Notes — every day</option>
                <option value="all_mcq">MCQs — every day</option>
              </select>
            </label>
            {genModeNeedsDay && (
              <label className="pp-gen-scope pp-gen-day" title="Plan day number (or click a day on the left)">
                <span className="pp-gen-scope-label">Day</span>
                <input
                  type="number"
                  min={1}
                  max={40}
                  placeholder={selectedDayPreview?.day != null ? String(selectedDayPreview.day) : '#'}
                  value={genDayNo}
                  disabled={loading || generating || !generateAllowed}
                  onChange={(e) => setGenDayNo(e.target.value)}
                />
              </label>
            )}
          </div>

          <div className="pp-upload-line pp-upload-ocr-row">
            <label className="pp-gen-scope" title="Engine for OCR / extract-text">
              <span className="pp-gen-scope-label">OCR</span>
              <select
                value={ocrProvider}
                disabled={loading || generating || reocrBusy || !generateAllowed}
                onChange={(e) => setOcrProvider(e.target.value)}
              >
                <option value="cursor">Cursor</option>
                <option value="local">Local</option>
                <option value="claude">Claude</option>
              </select>
            </label>
            {ocrProvider === 'local' && (
              <label className="pp-gen-scope pp-gen-model" title="Local vision model for OCR">
                <span className="pp-gen-scope-label">OCR model</span>
                <select
                  value={ocrLocalModel}
                  disabled={loading || generating || reocrBusy || !generateAllowed}
                  onChange={(e) => setOcrLocalModel(e.target.value)}
                >
                  {LOCAL_MODELS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </label>
            )}
            {ocrProvider === 'local' && (
              <label className="pp-gen-scope pp-gen-host" title="Host for OCR">
                <span className="pp-gen-scope-label">OCR host</span>
                <select
                  value={ocrLocalHost}
                  disabled={loading || generating || reocrBusy || !generateAllowed}
                  onChange={(e) => setOcrLocalHost(e.target.value)}
                >
                  {(localHosts.length ? localHosts : [
                    { key: 'mac', label: 'Mac' },
                    { key: 'gpu', label: 'Spc' },
                  ]).map((h) => (
                    <option key={h.key} value={h.key}>{h.label || h.key}</option>
                  ))}
                </select>
              </label>
            )}
            {selectedId && !generating && generateAllowed && (
              <button
                type="button"
                className="pp-btn pp-btn-primary"
                disabled={divideBusy || loading || reocrBusy || !chapterOcrText}
                onClick={handleDivideTopics}
                title="Split Chapter OCR into per-topic Text only — check each topic before Gen Notes"
              >
                {divideBusy
                  ? <><FontAwesomeIcon icon={faSpinner} spin /> Dividing…</>
                  : <><FontAwesomeIcon icon={faListCheck} /> Divide topics</>}
              </button>
            )}
            <button
              type="button"
              className="pp-btn pp-btn-primary"
              disabled={loading || generating || reocrBusy || !generateAllowed || (!files.length && !selectedId)}
              onClick={handleOcrBook}
              title="Upload (if needed) and OCR the book into Chapter OCR — first time or full re-read"
            >
              {reocrBusy && reocrScope === 'full'
                ? <><FontAwesomeIcon icon={faSpinner} spin /> OCR…</>
                : <><FontAwesomeIcon icon={faFont} /> OCR</>}
            </button>
          </div>

          <div className="pp-upload-line pp-upload-notes-row">
            <label className="pp-gen-scope" title="Engine for plan / notes / MCQ generation">
              <span className="pp-gen-scope-label">Notes</span>
              <select
                value={notesProvider}
                disabled={loading || generating || reocrBusy || !generateAllowed}
                onChange={(e) => setNotesProvider(e.target.value)}
              >
                <option value="cursor">Cursor</option>
                <option value="local">Local</option>
                <option value="claude">Claude</option>
              </select>
            </label>
            {notesProvider === 'local' && (
              <label className="pp-gen-scope pp-gen-model" title="Local model for notes generation">
                <span className="pp-gen-scope-label">Notes model</span>
                <select
                  value={notesLocalModel}
                  disabled={loading || generating || reocrBusy || !generateAllowed}
                  onChange={(e) => setNotesLocalModel(e.target.value)}
                >
                  {LOCAL_MODELS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </label>
            )}
            {notesProvider === 'local' && (
              <label className="pp-gen-scope pp-gen-host" title="Host for notes generation">
                <span className="pp-gen-scope-label">Notes host</span>
                <select
                  value={notesLocalHost}
                  disabled={loading || generating || reocrBusy || !generateAllowed}
                  onChange={(e) => setNotesLocalHost(e.target.value)}
                >
                  {(localHosts.length ? localHosts : [
                    { key: 'mac', label: 'Mac' },
                    { key: 'gpu', label: 'Spc' },
                  ]).map((h) => (
                    <option key={h.key} value={h.key}>{h.label || h.key}</option>
                  ))}
                </select>
              </label>
            )}
            <button
              type="button"
              className="pp-btn pp-btn-primary"
              disabled={loading || generating || reocrBusy || !generateAllowed || (!files.length && !selectedId)}
              onClick={handleGenerate}
              title={
                !generateAllowed
                  ? (capabilities.message || 'Generate disabled on this server')
                  : genMode === 'all_mcq' ? 'Generate MCQs for every plan day'
                  : genMode === 'all_notes' ? 'Generate notes for every plan day'
                    : genMode === 'day_notes' ? `Generate notes for day ${effectiveGenDay || '?'}`
                      : genMode === 'day_mcq' ? `Generate MCQs for day ${effectiveGenDay || '?'}`
                        : genMode === 'day' ? `Generate day ${effectiveGenDay || '?'} only`
                          : genMode === 'plan' ? 'Generate teaching plan only'
                            : 'Generate plan + every day'
              }
            >
              {loading || generating
                ? <><FontAwesomeIcon icon={faSpinner} spin /> …</>
                : <><FontAwesomeIcon icon={faWandMagicSparkles} /> Gen Notes</>}
            </button>
            {generating && (
              <button type="button" className="pp-btn pp-btn-danger" onClick={handleStop}>
                <FontAwesomeIcon icon={faStop} /> Stop
              </button>
            )}
          </div>

          <div className="pp-upload-line pp-upload-tools-row">
            {selectedId && !generating && generateAllowed && (
              <>
                <button
                  type="button"
                  className="pp-btn pp-btn-secondary"
                  disabled={reocrBusy || loading || divideBusy}
                  onClick={() => handleReOcrBook(null, { forceFull: true })}
                  title="Re-read the entire uploaded PDF/pages into Chapter OCR"
                >
                  {reocrBusy && reocrScope === 'full' && !loading
                    ? <><FontAwesomeIcon icon={faSpinner} spin /> Re-OCR…</>
                    : <><FontAwesomeIcon icon={faFont} /> Re-OCR book</>}
                </button>
                <div className="pp-reocr-pages">
                  <input
                    type="text"
                    className="pp-reocr-pages-input"
                    value={reocrPagesInput}
                    onChange={(e) => setReocrPagesInput(e.target.value)}
                    placeholder="p1, p3 or 1-3"
                    disabled={reocrBusy || loading}
                    title="Page numbers to re-OCR (p1, p2, 1-3, …)"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleReOcrPages();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="pp-btn pp-btn-secondary"
                    disabled={reocrBusy || loading || !reocrPagesInput.trim()}
                    onClick={handleReOcrPages}
                    title="Re-OCR only the page numbers entered (merges into Chapter OCR)"
                  >
                    {reocrBusy && reocrScope === 'pages' && !reocrPageBusy
                      ? <><FontAwesomeIcon icon={faSpinner} spin /> …</>
                      : <><FontAwesomeIcon icon={faArrowsRotate} /> Pages</>}
                  </button>
                </div>
              </>
            )}
            {selectedId && !generating && !loading && generateAllowed && outputs.plan && (
              <>
                <button
                  type="button"
                  className="pp-btn pp-btn-secondary"
                  onClick={() => handleGenerateScoped({ step: 'mcq' }, 'Generating MCQs for every plan day…')}
                  title="MCQs only — every plan day that has an MCQ step"
                >
                  All MCQs
                </button>
                <button
                  type="button"
                  className="pp-btn pp-btn-secondary"
                  onClick={() => handleGenerateScoped({ step: 'notes' }, 'Generating notes for every plan day…')}
                  title="Notes only — every plan day that has a notes step"
                >
                  All notes
                </button>
                {(outputs.fullNotes || (days || []).some((d) => d.steps?.some((s) => s.key === 'notes' && s.done))) && (
                  <button
                    type="button"
                    className="pp-btn pp-btn-secondary"
                    onClick={handleRebuildFullNotes}
                    title="Rebuild printable packs from saved day notes (no AI regenerate)"
                  >
                    <FontAwesomeIcon icon={faArrowsRotate} /> Update Preview
                  </button>
                )}
              </>
            )}
            {selectedId && !generating && !loading && generateAllowed && selectedDayPreview?.day && (
              <>
                <button
                  type="button"
                  className="pp-btn pp-btn-primary"
                  onClick={() => handleRegenerateDay({ stepOnly: false })}
                  title="Re-run only the selected plan day (all its steps)"
                >
                  <FontAwesomeIcon icon={faWandMagicSparkles} /> Day {selectedDayPreview.day}
                </button>
                {(days.find((d) => d.lesson_no === selectedDayPreview.day)?.steps || [])
                  .some((s) => s.key === 'notes') && (
                  <button
                    type="button"
                    className="pp-btn pp-btn-secondary"
                    onClick={() => handleRegenerateDay({ stepKey: 'notes' })}
                    title={`Update notes for Day ${selectedDayPreview.day} only`}
                  >
                    <FontAwesomeIcon icon={faArrowsRotate} /> Day {selectedDayPreview.day} notes
                  </button>
                )}
                {(days.find((d) => d.lesson_no === selectedDayPreview.day)?.steps || [])
                  .some((s) => s.key === 'mcq') && (
                  <button
                    type="button"
                    className="pp-btn pp-btn-secondary"
                    onClick={() => handleRegenerateDay({ stepKey: 'mcq' })}
                    title={`MCQs for Day ${selectedDayPreview.day} only`}
                  >
                    Day {selectedDayPreview.day} MCQs
                  </button>
                )}
                <button
                  type="button"
                  className="pp-btn pp-btn-secondary"
                  onClick={() => handleRegenerateDay({ stepOnly: true })}
                  title="Re-run only the previewed step"
                >
                  <FontAwesomeIcon icon={faWandMagicSparkles} />{' '}
                  {selectedDayPreview.step || 'step'}
                </button>
              </>
            )}
          </div>

          {files.length > 0 && (
            <ul className="pp-file-list">
              {files.map((f) => <li key={f.name}>{f.name}</li>)}
            </ul>
          )}
          {(loading || generating) && (
            <div className="pp-progress-bar">
              <div className="pp-progress-fill" style={{ width: `${progressPct()}%` }} />
            </div>
          )}
          {genStep && generating && (
            <div className="pp-chapter-meta" style={{ marginTop: 6 }}>
              {queued ? 'Queue: ' : 'Step: '}{genStep}
            </div>
          )}
          {(pipelineStatus?.stage || (generating && genStep)) && (
            <div className="pp-pipeline-stage" style={{ marginTop: 6 }} role="status">
              Pipeline:{' '}
              <strong>
                {pipelineStageLabel(pipelineStatus?.stage)
                  || (generating ? 'Running' : '—')}
              </strong>
              {pipelineStatus?.topicCount != null
                ? ` · ${pipelineStatus.topicCount} topic(s)`
                : ''}
            </div>
          )}
          {selectedId && qaSummary && qaSummary.total > 0 && (
            <div className="pp-qa-rollup" style={{ marginTop: 6 }} role="status">
              QA:{' '}
              <strong>{qaSummary.byStatus?.open || 0} open</strong>
              {qaSummary.needsHuman ? ` · ${qaSummary.needsHuman} need you` : ''}
              {qaSummary.byStatus?.auto_fixed ? ` · ${qaSummary.byStatus.auto_fixed} auto-fixed` : ''}
              {qaSummary.byStatus?.reverted ? ` · ${qaSummary.byStatus.reverted} reverted` : ''}
              {qaSummary.openCritical ? ` · ${qaSummary.openCritical} critical` : ''}
              <span className="pp-qa-rollup-hint"> — open a day and click Notes QA / MCQ QA</span>
            </div>
          )}
          {(runTiming?.total_elapsed_sec != null || runTiming?.total_elapsed_label) && (
            <div className="pp-run-timing" style={{ marginTop: 6 }}>
              Time taken:{' '}
              <strong>
                {runTiming.total_elapsed_label
                  || formatMinSec(runTiming.total_elapsed_sec)}
              </strong>
              {generating ? (queued ? ' (waiting in queue…)' : ' (running…)') : ''}
            </div>
          )}
        </div>
      </div>
      </div>

      <div className="pp-workspace">
        <aside className="pp-plan-rail">
          <div className="pp-plan-rail-head pp-rail-head">
            <h2>Plan overview {className && subject ? `— ${className}` : ''}</h2>
            <input
              type="search"
              className="pp-plan-search"
              placeholder="Search topics"
              value={planSearch}
              onChange={(e) => setPlanSearch(e.target.value)}
            />
            {chapters.length > 0 && (
              <div className="pp-print-book">
                <div className="pp-print-book-actions">
                  <button
                    type="button"
                    className="pp-btn pp-btn-secondary pp-print-book-btn"
                    disabled={printBusy || printChapterIds.length < 1}
                    onClick={() => handleDownloadCombinedPackPdf('notes')}
                    title="Download Notes PDF for the ticked chapters"
                  >
                    {printBusy
                      ? <><FontAwesomeIcon icon={faSpinner} spin /> Building…</>
                      : <><FontAwesomeIcon icon={faDownload} /> Notes ({printChapterIds.length})</>}
                  </button>
                  <button
                    type="button"
                    className="pp-btn pp-btn-secondary pp-print-book-btn"
                    disabled={printBusy || printChapterIds.length < 1}
                    onClick={() => handleDownloadCombinedPackPdf('mcqs')}
                    title="Download MCQs PDF for the ticked chapters"
                  >
                    {printBusy
                      ? <><FontAwesomeIcon icon={faSpinner} spin /> Building…</>
                      : <><FontAwesomeIcon icon={faDownload} /> MCQs ({printChapterIds.length})</>}
                  </button>
                </div>
                <p className="pp-print-book-hint">
                  Tick chapters, then download Notes PDF and/or MCQs PDF for students.
                </p>
              </div>
            )}
          </div>
          <div className="pp-rail-scroll">
            {!chapters.length ? (
              <div className="pp-empty">No chapters yet. Upload and generate your first pack above.</div>
            ) : (
              chapters.map((ch) => {
                const isSelected = selectedId === ch.id;
                const open = expandedChapterId === ch.id;
                const chapterLabel = `${ch.chapter_no}. ${ch.title}`;
                return (
                  <div key={ch.id} className={`pp-tree-chapter${open ? ' open' : ''}${isSelected ? ' selected' : ''}`}>
                    <div
                      className={`pp-tree-chapter-row${isSelected ? ' active' : ''}`}
                      onClick={() => selectAndToggleChapter(ch.id)}
                      role="button"
                      tabIndex={0}
                      aria-expanded={open}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          selectAndToggleChapter(ch.id);
                        }
                      }}
                    >
                      <label
                        className="pp-print-check"
                        title="Include in combined print PDF"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={printChapterIds.includes(ch.id)}
                          onChange={(e) => togglePrintChapter(ch.id, e)}
                          aria-label={`Print chapter ${ch.chapter_no}`}
                        />
                      </label>
                      <span className="pp-tree-chevron" aria-hidden="true">
                        <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
                      </span>
                      <span className="pp-tree-chapter-title" title={chapterLabel}>
                        {chapterLabel}
                      </span>
                      <span className={`pp-badge pp-badge-${ch.status || 'uploaded'}`}>
                        {statusLabel(ch.status)}
                      </span>
                      {canDeleteChapter && (
                        <button
                          type="button"
                          className="pp-chapter-delete"
                          title="Delete chapter"
                          aria-label={`Delete ${chapterLabel}`}
                          onClick={(e) => handleDeleteChapter(ch, e)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>

                    {Array.isArray(ch.note_versions) && ch.note_versions.length > 0 && (
                      <div
                        className="pp-note-versions"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        {ch.note_versions.map((v) => (
                          <div
                            key={v.id}
                            className={`pp-note-version-chip${v.is_active ? ' active' : ''}`}
                          >
                            <button
                              type="button"
                              className="pp-note-version-tag"
                              title={
                                v.model
                                  ? `${v.label} · ${v.model}${v.is_active ? ' (active)' : ''} — click to activate`
                                  : `${v.label}${v.is_active ? ' (active)' : ''} — click to activate`
                              }
                              onClick={(e) => handleActivateNoteVersion(ch.id, v.id, e)}
                            >
                              {v.label}
                            </button>
                            <button
                              type="button"
                              className="pp-note-version-action"
                              title={`Rename ${v.label}`}
                              aria-label={`Rename ${v.label}`}
                              onClick={(e) => handleRenameNoteVersion(ch.id, v, e)}
                            >
                              <FontAwesomeIcon icon={faPenToSquare} />
                            </button>
                            <button
                              type="button"
                              className="pp-note-version-action danger"
                              title={`Delete ${v.label}`}
                              aria-label={`Delete ${v.label}`}
                              onClick={(e) => handleDeleteNoteVersion(ch.id, v, e)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {open && isSelected && (
                      <div className="pp-tree-children">
                        <button
                          type="button"
                          className={`pp-plan-row${previewKey === 'pages' ? ' active' : ''}`}
                          onClick={() => loadPreview(selectedId, 'pages', 'Uploaded pages', 'pages', null)}
                        >
                          <span className="pp-plan-num">
                            <FontAwesomeIcon icon={faImages} />
                          </span>
                          <span className="pp-plan-topic">Uploaded pages</span>
                          <span className="pp-plan-frac">{chapterPages.length || '—'}</span>
                        </button>
                        <button
                          type="button"
                          className={`pp-plan-row${previewKey === 'ocr' ? ' active' : ''}`}
                          onClick={() => loadPreview(selectedId, 'ocr', 'Chapter OCR', 'ocr', null)}
                        >
                          <span className="pp-plan-num">
                            <FontAwesomeIcon icon={faFont} />
                          </span>
                          <span className="pp-plan-topic">Chapter OCR</span>
                          <span className="pp-plan-frac">
                            {chapterOcrText.length
                              ? (chapterOcrText.length >= 1000
                                ? `${(chapterOcrText.length / 1000).toFixed(1)}k`
                                : String(chapterOcrText.length))
                              : '—'}
                          </span>
                        </button>
                        {outputs.plan && (
                          <button
                            type="button"
                            className={`pp-plan-row${previewKey === 'plan' ? ' active' : ''}`}
                            onClick={() => loadPreview(selectedId, 'plan', 'Teaching plan', 'plan', null)}
                          >
                            <span className="pp-plan-num">P</span>
                            <span className="pp-plan-topic">Teaching plan</span>
                            <span className="pp-plan-frac">1/1</span>
                          </button>
                        )}
                        {outputs.fullNotes && (
                          <button
                            type="button"
                            className={`pp-plan-row${previewKey === 'full_notes' ? ' active' : ''}`}
                            onClick={() => loadPreview(selectedId, 'full_notes', 'Full chapter notes', 'full_notes', null)}
                          >
                            <span className="pp-plan-num">N</span>
                            <span className="pp-plan-topic">Full chapter notes</span>
                            <span className="pp-plan-frac">1/1</span>
                          </button>
                        )}
                        {outputs.fullNotes && (
                          <button
                            type="button"
                            className={`pp-plan-row${previewKey === 'full_short' ? ' active' : ''}`}
                            onClick={() => loadPreview(selectedId, 'full_short', 'Short answers', 'full_notes', null)}
                          >
                            <span className="pp-plan-num">S</span>
                            <span className="pp-plan-topic">Short answers</span>
                            <span className="pp-plan-frac">1/1</span>
                          </button>
                        )}
                        {outputs.fullNotes && (
                          <button
                            type="button"
                            className={`pp-plan-row${previewKey === 'exercise_notes' ? ' active' : ''}`}
                            onClick={() => loadPreview(selectedId, 'exercise_notes', 'Exercise notes', 'full_notes', null)}
                          >
                            <span className="pp-plan-num">E</span>
                            <span className="pp-plan-topic">Exercise notes</span>
                            <span className="pp-plan-frac">1/1</span>
                          </button>
                        )}
                        {outputs.fullNotes && (
                          <button
                            type="button"
                            className={`pp-plan-row${previewKey === 'question_bank' ? ' active' : ''}`}
                            onClick={() => loadPreview(selectedId, 'question_bank', 'Question bank', 'full_notes', null)}
                          >
                            <span className="pp-plan-num">Q</span>
                            <span className="pp-plan-topic">Question bank</span>
                            <span className="pp-plan-frac">1/1</span>
                          </button>
                        )}
                        {outputs.fullNotes && (
                          <button
                            type="button"
                            className={`pp-plan-row${previewKey === 'qb_notes' ? ' active' : ''}`}
                            onClick={() => loadPreview(selectedId, 'qb_notes', 'Question bank + exercise notes', 'full_notes', null)}
                          >
                            <span className="pp-plan-num">B</span>
                            <span className="pp-plan-topic">Question bank + exercise notes</span>
                            <span className="pp-plan-frac">1/1</span>
                          </button>
                        )}
                        {outputs.fullWorksheet && (
                          <button
                            type="button"
                            className={`pp-plan-row${previewKey === 'full_worksheet' ? ' active' : ''}`}
                            onClick={() => loadPreview(selectedId, 'full_worksheet', 'Workbook (full chapter)', 'full_worksheet', null)}
                          >
                            <span className="pp-plan-num">W</span>
                            <span className="pp-plan-topic">Full workbook</span>
                            <span className="pp-plan-frac">1/1</span>
                          </button>
                        )}
                        {outputs.fullMcq && (
                          <button
                            type="button"
                            className={`pp-plan-row${previewKey === 'full_mcq' ? ' active' : ''}`}
                            onClick={() => loadPreview(selectedId, 'full_mcq', 'MCQs (full chapter)', 'full_mcq', null)}
                          >
                            <span className="pp-plan-num">M</span>
                            <span className="pp-plan-topic">Full MCQs</span>
                            <span className="pp-plan-frac">1/1</span>
                          </button>
                        )}
                        {filteredDays.map((d) => {
                          const { done, total } = dayProgress(d);
                          const step = preferredDayStep(d);
                          const key = step ? `day:${d.lesson_no}:${step}` : `day:${d.lesson_no}`;
                          const ready = !!step || !!d.hasTopicOcr;
                          const topicOpen = expandedTopicDays.has(d.lesson_no);
                          const isExercise = /exercise\s*q\/?a/i.test(String(d.label || ''))
                            || /^exercise\b/i.test(String(d.topic || ''));
                          const childSteps = [
                            { key: 'topic_ocr', label: 'Text', always: true },
                            { key: 'notes', label: 'Notes' },
                            { key: 'mcq', label: 'MCQs' },
                            { key: 'worksheet', label: isExercise ? 'Solutions' : 'Worksheet' },
                            { key: 'test', label: 'Test' },
                          ].filter((c) => {
                            if (c.key === 'topic_ocr') return true;
                            return (d.steps || []).some((s) => s.key === c.key);
                          });
                          return (
                            <div key={d.lesson_no} className="pp-tree-topic-block">
                              <button
                                type="button"
                                className={`pp-plan-row${previewKey === key || String(previewKey || '').startsWith(`day:${d.lesson_no}:`) ? ' active' : ''}${!ready ? ' muted' : ''}`}
                                onClick={() => {
                                  setExpandedTopicDays((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(d.lesson_no)) next.delete(d.lesson_no);
                                    else next.add(d.lesson_no);
                                    return next;
                                  });
                                  if (d.hasTopicOcr) {
                                    loadPreview(
                                      selectedId,
                                      `day:${d.lesson_no}:topic_ocr`,
                                      `${d.lesson_no}. ${d.topic || d.label || 'Topic'} — Text`,
                                      'topic_ocr',
                                      d.lesson_no,
                                    );
                                  } else if (step) {
                                    loadPreview(
                                      selectedId,
                                      key,
                                      `${d.lesson_no}. ${d.topic || d.label || step}`,
                                      step,
                                      d.lesson_no,
                                    );
                                  }
                                }}
                              >
                                <span className="pp-plan-num">{d.lesson_no}</span>
                                <span className="pp-plan-topic" dir="auto">
                                  {d.topic || d.label || `Day ${d.lesson_no}`}
                                  {d.label && d.topic && d.label !== d.topic ? (
                                    <em className="pp-plan-label">{d.label}</em>
                                  ) : null}
                                </span>
                                <span className="pp-plan-frac">{total ? `${done}/${total}` : (d.hasTopicOcr ? 'OCR' : '—')}</span>
                              </button>
                              {topicOpen && (
                                <div className="pp-tree-topic-children">
                                  {childSteps.map((c) => {
                                    const doneStep = c.key === 'topic_ocr'
                                      ? !!d.hasTopicOcr
                                      : (d.steps || []).some((s) => s.key === c.key && s.done);
                                    const childKey = `day:${d.lesson_no}:${c.key}`;
                                    return (
                                      <button
                                        key={c.key}
                                        type="button"
                                        className={`pp-plan-row pp-plan-row-child${previewKey === childKey ? ' active' : ''}${!doneStep ? ' muted' : ''}`}
                                        disabled={c.key !== 'topic_ocr' && !doneStep}
                                        onClick={() => {
                                          if (c.key === 'topic_ocr') {
                                            loadPreview(
                                              selectedId,
                                              childKey,
                                              `${d.lesson_no}. ${d.topic || ''} — Text`,
                                              'topic_ocr',
                                              d.lesson_no,
                                            );
                                            return;
                                          }
                                          if (!doneStep) return;
                                          loadPreview(
                                            selectedId,
                                            childKey,
                                            `${d.lesson_no}. ${d.topic || ''} — ${c.label}`,
                                            c.key,
                                            d.lesson_no,
                                          );
                                        }}
                                      >
                                        <span className="pp-plan-num">{c.label.slice(0, 1)}</span>
                                        <span className="pp-plan-topic">{c.label}</span>
                                        <span className="pp-plan-frac">{doneStep ? '✓' : '—'}</span>
                                      </button>
                                    );
                                  })}
                                  {[
                                    { kind: 'notes', tag: 'Q', label: 'Notes QA', report: d.notes_qa },
                                    { kind: 'mcq', tag: 'K', label: 'MCQ QA', report: d.mcq_qa },
                                  ].filter((q) => q.report).map((q) => (
                                    <button
                                      key={q.kind}
                                      type="button"
                                      className={`pp-plan-row pp-plan-row-child${previewKey === `day:${d.lesson_no}:qa_${q.kind}` ? ' active' : ''}`}
                                      title={q.report.summary || q.label}
                                      onClick={() => loadQaPanel(selectedId, d.lesson_no, q.kind)}
                                    >
                                      <span className="pp-plan-num">{q.tag}</span>
                                      <span className="pp-plan-topic">
                                        {q.label} {q.report.ok ? 'OK' : 'Issues'}
                                        {q.report.auto_fixed ? ' (fixed)' : ''}
                                        {q.report.repairs_reverted?.length ? ' (reverted)' : ''}
                                      </span>
                                      <span className="pp-plan-frac">{q.report.ok ? '✓' : '!'}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {chapterStructure?.exercise && (
                          <div className="pp-tree-exercise-meta">
                            <div className="pp-empty pp-empty-nested" style={{ textAlign: 'left' }}>
                              Exercise structure:{' '}
                              {chapterStructure.exercise.mcqCount
                                ? `${chapterStructure.exercise.mcqCount} MCQs`
                                : 'MCQs —'}
                              {(chapterStructure.exercise.sections || []).map((s) => (
                                <span key={s.id}> · {s.title}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {!outputs.plan && !days.length && (
                          <div className="pp-empty pp-empty-nested">
                            {ch.status === 'generating' || generating
                              ? 'Detecting topics / generating…'
                              : 'No topics or plan days yet — upload PDF and generate.'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </aside>

        {selectedId ? (
          <section
            ref={previewPaneRef}
            className={`pp-preview-pane${previewFullscreen ? ' is-preview-fullscreen' : ''}`}
          >
            <div className="pp-preview-bar">
              <div className="pp-preview-bar-title" dir="auto">
                <FontAwesomeIcon icon={previewKey === 'pages' ? faImages : faEye} />
                {previewTitle || (selected ? `${selected.chapter_no}. ${selected.title}` : 'Preview')}
              </div>
              <div className="pp-preview-bar-actions">
                {(previewMode === 'pdf' || previewMode === 'shots' || previewMode === 'markdown'
                  || previewMode === 'ocr' || previewKey === 'plan'
                  || String(previewKey || '').includes('topic_ocr')) && previewKey && previewKey !== 'pages' && (
                  <div className="pp-preview-mode-toggle" role="group" aria-label="Preview mode">
                    <button
                      type="button"
                      className={`pp-btn pp-btn-secondary${previewViewMode === 'text' || previewMode === 'ocr' || previewMode === 'markdown' ? ' active' : ''}`}
                      onClick={async () => {
                        setPreviewViewMode('text');
                        if (previewMode === 'pdf' || previewMode === 'shots') {
                          const sel = parsePreviewSelection(previewKey);
                          if (sel?.step === 'topic_ocr') return;
                          if (previewKey === 'ocr') return;
                          try {
                            setPreviewLoading(true);
                            const step = sel?.step
                              || (previewKey === 'plan' ? 'plan'
                                : previewKey === 'full_notes' || previewKey === 'full_short'
                                  || previewKey === 'exercise_notes' || previewKey === 'question_bank'
                                  || previewKey === 'qb_notes' ? 'full_notes'
                                  : previewKey === 'full_mcq' ? 'full_mcq'
                                    : previewKey === 'full_worksheet' ? 'full_worksheet'
                                      : null);
                            if (!step) return;
                            const out = await plansPrepApi.getOutput(
                              selectedId,
                              step,
                              sel?.day != null ? sel.day : null,
                            );
                            setPreviewText(String(out.text || out.text_content || '').trim());
                            setPreviewMode('markdown');
                          } catch (e) {
                            setPreviewErr(e.message || 'Could not load text.');
                          } finally {
                            setPreviewLoading(false);
                          }
                        }
                      }}
                      title="Show structured / canonical text"
                    >
                      Text
                    </button>
                    <button
                      type="button"
                      className={`pp-btn pp-btn-secondary${previewViewMode === 'pdf' || previewMode === 'pdf' || previewMode === 'shots' ? ' active' : ''}`}
                      disabled={previewKey === 'ocr' || String(previewKey || '').includes('topic_ocr') || previewKey === 'plan'}
                      onClick={() => {
                        setPreviewViewMode('pdf');
                        if (previewKey && selectedId) {
                          const sel = parsePreviewSelection(previewKey);
                          const step = sel?.step
                            || (previewKey === 'full_notes' || previewKey === 'full_short'
                              || previewKey === 'exercise_notes' || previewKey === 'question_bank'
                              || previewKey === 'qb_notes' ? 'full_notes'
                              : previewKey === 'full_mcq' ? 'full_mcq'
                                : previewKey === 'full_worksheet' ? 'full_worksheet'
                                  : previewKey);
                          loadPreview(
                            selectedId,
                            previewKey,
                            previewTitle,
                            step,
                            sel?.day != null ? sel.day : null,
                            { wantPdf: true },
                          );
                        }
                      }}
                      title="Generate / show PDF print preview"
                    >
                      PDF
                    </button>
                  </div>
                )}
                {previewKey === 'ocr' && selectedId && (
                  <div className="pp-downloads pp-downloads-inline pp-reocr-bar">
                    {!ocrEditing ? (
                      <button
                        type="button"
                        className="pp-btn pp-btn-secondary"
                        disabled={reocrBusy || generating || loading || previewLoading}
                        onClick={startOcrEdit}
                        title="Edit Chapter OCR text to fix mistakes"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} /> Edit OCR
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="pp-btn pp-btn-primary"
                          disabled={ocrSaving || !ocrDirty}
                          onClick={saveOcrText}
                          title="Save corrected OCR text"
                        >
                          {ocrSaving
                            ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</>
                            : <><FontAwesomeIcon icon={faFloppyDisk} /> Save</>}
                        </button>
                        <button
                          type="button"
                          className="pp-btn pp-btn-secondary"
                          disabled={ocrSaving}
                          onClick={cancelOcrEdit}
                          title="Discard edits"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    <input
                      type="text"
                      className="pp-reocr-pages-input"
                      value={reocrPagesInput}
                      onChange={(e) => setReocrPagesInput(e.target.value)}
                      placeholder="p1, p3 or 1-3"
                      disabled={reocrBusy || generating || loading || ocrEditing}
                      title="Page numbers to re-OCR (p1, p2, 1-3, …)"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleReOcrPages();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="pp-btn pp-btn-secondary"
                      disabled={reocrBusy || generating || loading || !generateAllowed || !reocrPagesInput.trim() || ocrEditing}
                      onClick={handleReOcrPages}
                      title="Re-OCR only these pages into Chapter OCR"
                    >
                      {reocrBusy && reocrScope === 'pages' && !reocrPageBusy
                        ? <><FontAwesomeIcon icon={faSpinner} spin /> …</>
                        : <><FontAwesomeIcon icon={faArrowsRotate} /> Re-OCR pages</>}
                    </button>
                    <button
                      type="button"
                      className="pp-btn pp-btn-primary"
                      disabled={reocrBusy || generating || loading || !generateAllowed || ocrEditing}
                      onClick={() => handleReOcrBook(null, { forceFull: true })}
                      title="Re-read entire PDF/pages into Chapter OCR (yellow equation boxes included for science)"
                    >
                      {reocrBusy && reocrScope === 'full'
                        ? <><FontAwesomeIcon icon={faSpinner} spin /> Re-OCR…</>
                        : <><FontAwesomeIcon icon={faFont} /> Re-OCR book</>}
                    </button>
                  </div>
                )}
                {previewKey === 'pages' && chapterPages.length > 0 && (
                  <div className="pp-downloads pp-downloads-inline">
                    <button
                      type="button"
                      className="pp-btn pp-btn-primary"
                      disabled={pagesBusy || pagesDownloading || generating}
                      onClick={handleDownloadAllPages}
                      title="Save all images as p1, p2, … — pick the folder"
                    >
                      {pagesDownloading
                        ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</>
                        : <><FontAwesomeIcon icon={faDownload} /> Download all</>}
                    </button>
                  </div>
                )}
                {selectedId && selectedDayPreview?.day && !generating && !loading && (
                  <div className="pp-downloads pp-downloads-inline pp-day-regen">
                    <button
                      type="button"
                      className="pp-btn pp-btn-primary"
                      onClick={() => handleRegenerateDay({ stepOnly: false })}
                      title="Regenerate this day only (all steps)"
                    >
                      <FontAwesomeIcon icon={faWandMagicSparkles} /> Day {selectedDayPreview.day}
                    </button>
                    {(days.find((d) => d.lesson_no === selectedDayPreview.day)?.steps || [])
                      .some((s) => s.key === 'notes') && (
                      <button
                        type="button"
                        className="pp-btn pp-btn-secondary"
                        onClick={() => handleRegenerateDay({ stepKey: 'notes' })}
                        title="Update notes for this day only"
                      >
                        <FontAwesomeIcon icon={faArrowsRotate} /> Update
                      </button>
                    )}
                    {(days.find((d) => d.lesson_no === selectedDayPreview.day)?.steps || [])
                      .some((s) => s.key === 'mcq') && (
                      <button
                        type="button"
                        className="pp-btn pp-btn-secondary"
                        onClick={() => handleRegenerateDay({ stepKey: 'mcq' })}
                        title="MCQs for this day only"
                      >
                        MCQs
                      </button>
                    )}
                    <button
                      type="button"
                      className="pp-btn pp-btn-secondary"
                      onClick={() => handleRegenerateDay({ stepOnly: true })}
                      title="Regenerate only the step shown in preview"
                    >
                      <FontAwesomeIcon icon={faWandMagicSparkles} />{' '}
                      {selectedDayPreview.step || 'step'}
                    </button>
                  </div>
                )}
                {selectedId && previewKey !== 'pages' && (outputs.plan || outputs.notes || outputs.worksheet || outputs.mcq || outputs.tests.length > 0) && (
                  <div className="pp-downloads pp-downloads-inline">
                    {outputs.plan && (
                      <>
                        <button type="button" className="pp-btn pp-btn-secondary" onClick={handleCopyPlan}>
                          <FontAwesomeIcon icon={faCopy} /> Copy plan
                        </button>
                        <button type="button" className="pp-btn pp-btn-secondary" onClick={handleDownloadPlan}>
                          <FontAwesomeIcon icon={faDownload} /> Plan .txt
                        </button>
                      </>
                    )}
                    {outputs.fullNotes && (
                      <>
                        <button
                          type="button"
                          className="pp-btn pp-btn-secondary"
                          onClick={handleCopyFullChapterText}
                          title="Copy full chapter notes with bold text and tables, for pasting into Word"
                        >
                          <FontAwesomeIcon icon={faCopy} /> Copy full text
                        </button>
                        {!outputs.fullWorksheet && (
                          <button type="button" className="pp-btn pp-btn-primary" onClick={handleDownloadNotesPack}>
                            <FontAwesomeIcon icon={faDownload} /> Zip File
                          </button>
                        )}
                        {(previewKey === 'full_notes' || previewKey === 'full_short'
                          || previewKey === 'exercise_notes'
                          || previewKey === 'question_bank' || previewKey === 'qb_notes')
                          && !generating && !loading && (
                          <button
                            type="button"
                            className="pp-btn pp-btn-primary"
                            onClick={handleRebuildFullNotes}
                            title="Rebuild printable packs from saved day notes (Full notes / Short answers / Exercise notes / Question bank)"
                          >
                            <FontAwesomeIcon icon={faArrowsRotate} /> Update Preview
                          </button>
                        )}
                      </>
                    )}
                    {outputs.fullWorksheet && (
                      <>
                        <button type="button" className="pp-btn pp-btn-primary" onClick={handleDownloadWorkbookPack}>
                          <FontAwesomeIcon icon={faDownload} /> EduPage workbook
                        </button>
                        <button type="button" className="pp-btn pp-btn-secondary" onClick={() => handleDownloadDocx('full_worksheet', null, 'Workbook')}>
                          <FontAwesomeIcon icon={faPenToSquare} /> Workbook
                        </button>
                      </>
                    )}
                    {!outputs.fullWorksheet && outputs.dayWorksheets?.length > 0 && outputs.dayWorksheets.map((dayNo) => (
                      <button
                        key={`ws-${dayNo}`}
                        type="button"
                        className="pp-btn pp-btn-secondary"
                        onClick={() => handleDownloadDocx('worksheet', dayNo, `Worksheet Day ${dayNo}`)}
                      >
                        <FontAwesomeIcon icon={faPenToSquare} /> WS Day {dayNo}
                      </button>
                    ))}
                    {outputs.tests.map((dayNo) => (
                      <button
                        key={dayNo}
                        type="button"
                        className="pp-btn pp-btn-secondary"
                        onClick={() => handleDownloadDocx('test', dayNo, `Test Day ${dayNo}`)}
                      >
                        <FontAwesomeIcon icon={faClipboardCheck} /> Test {dayNo}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  className="pp-btn pp-btn-secondary pp-preview-fs-btn"
                  onClick={togglePreviewFullscreen}
                  title={previewFullscreen ? 'Exit full screen (Esc)' : 'Full screen preview'}
                  aria-pressed={previewFullscreen}
                >
                  <FontAwesomeIcon icon={previewFullscreen ? faCompress : faExpand} />
                  {previewFullscreen ? ' Exit' : ' Full screen'}
                </button>
              </div>
            </div>

            {previewKey === 'pages' ? (
              <div className="pp-preview-doc pp-preview-pages">
                {pagesLoading ? (
                  <div className="pp-empty"><FontAwesomeIcon icon={faSpinner} spin /> Loading pages…</div>
                ) : !chapterPages.length ? (
                  <div className="pp-empty">No page images yet. Upload a PDF or screenshots above, or add pages below.</div>
                ) : (
                  <div className="pp-pages-grid pp-pages-grid-lg">
                    {chapterPages.map((p) => (
                      <div key={p.page} className="pp-page-card">
                        <button
                          type="button"
                          className="pp-page-thumb"
                          title={`View page ${p.page}`}
                          disabled={!p.blobUrl}
                          onClick={() => p.blobUrl && setPageLightbox(p.page)}
                        >
                          {p.blobUrl ? (
                            <img src={p.blobUrl} alt={`Page ${p.page}`} />
                          ) : (
                            <span className="pp-empty">Page {p.page}</span>
                          )}
                        </button>
                        <div className="pp-page-card-bar">
                          <span>p.{p.page}</span>
                          <div className="pp-page-card-actions">
                            <button
                              type="button"
                              className="pp-page-view"
                              title={`View page ${p.page}`}
                              disabled={!p.blobUrl}
                              onClick={() => setPageLightbox(p.page)}
                            >
                              <FontAwesomeIcon icon={faExpand} />
                            </button>
                            <button
                              type="button"
                              className="pp-page-reocr"
                              title={`Re-OCR page ${p.page} into Chapter OCR`}
                              disabled={reocrBusy || pagesBusy || generating || !generateAllowed}
                              onClick={() => handleReOcrPage(p.page)}
                            >
                              {reocrPageBusy === p.page
                                ? <FontAwesomeIcon icon={faSpinner} spin />
                                : <FontAwesomeIcon icon={faFont} />}
                            </button>
                            <button
                              type="button"
                              className="pp-page-del"
                              title={`Delete page ${p.page}`}
                              disabled={pagesBusy || generating || chapterPages.length <= 1}
                              onClick={() => handleDeletePage(p.page)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="pp-pages-actions">
                  <input
                    ref={addPagesRef}
                    type="file"
                    multiple
                    accept="image/png,image/jpeg,image/webp"
                    hidden
                    onChange={(e) => handleAddPages(e.target.files)}
                  />
                  <button
                    type="button"
                    className="pp-btn pp-btn-secondary"
                    disabled={!selectedId || pagesBusy || pagesDownloading || generating || !chapterPages.length}
                    onClick={handleDownloadAllPages}
                    title="Save all uploaded page images (p1, p2, …) — you will pick the folder"
                  >
                    {pagesDownloading
                      ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</>
                      : <><FontAwesomeIcon icon={faDownload} /> Download all</>}
                  </button>
                  <button
                    type="button"
                    className="pp-btn pp-btn-secondary"
                    disabled={!selectedId || pagesBusy || pagesDownloading || generating}
                    onClick={() => addPagesRef.current?.click()}
                  >
                    {pagesBusy
                      ? <><FontAwesomeIcon icon={faSpinner} spin /> Updating…</>
                      : <><FontAwesomeIcon icon={faPlus} /> Add pages</>}
                  </button>
                  <span className="pp-pages-hint">
                    Download all saves p1, p2, … into a folder you choose (Chrome/Edge). Use the font icon on a page to Re-OCR that page only, or delete/add screenshots then regenerate.
                  </span>
                </div>
              </div>
            ) : previewLoading ? (
              <div className="pp-preview-doc">
                <div className="pp-empty">
                  <FontAwesomeIcon icon={faSpinner} spin /> {previewBusyHint}
                </div>
              </div>
            ) : previewMode === 'qa' ? (
              <div className="pp-preview-doc pp-qa-panel">
                {qaLoading ? (
                  <div className="pp-empty"><FontAwesomeIcon icon={faSpinner} spin /> Loading QA issues…</div>
                ) : (
                  <>
                    <div className="pp-qa-bar">
                      <div className="pp-qa-counts">
                        {(() => {
                          const open = (qaPanel?.issues || []).filter((i) => i.status === 'open');
                          const human = (qaPanel?.issues || []).filter((i) => i.status === 'needs_human');
                          const reverted = (qaPanel?.issues || []).filter((i) => i.status === 'reverted');
                          const ruled = (qaPanel?.issues || []).filter((i) => i.status === 'accepted' || i.status === 'wontfix');
                          return (
                            <>
                              <span className="pp-qa-chip pp-qa-chip-open">{open.length} open</span>
                              <span className="pp-qa-chip pp-qa-chip-human">{human.length} need you</span>
                              {reverted.length ? <span className="pp-qa-chip pp-qa-chip-rev">{reverted.length} repair reverted</span> : null}
                              {ruled.length ? <span className="pp-qa-chip pp-qa-chip-done">{ruled.length} ruled</span> : null}
                            </>
                          );
                        })()}
                      </div>
                      <div className="pp-qa-actions">
                        <button
                          type="button"
                          className="pp-btn pp-btn-secondary"
                          disabled={qaBusy === 'recheck'}
                          title="Re-check saved text against the book. No AI — instant and free."
                          onClick={() => handleQaRecheck(false)}
                        >
                          {qaBusy === 'recheck'
                            ? <><FontAwesomeIcon icon={faSpinner} spin /> Checking…</>
                            : <><FontAwesomeIcon icon={faClipboardCheck} /> Re-check (free)</>}
                        </button>
                        <button
                          type="button"
                          className="pp-btn pp-btn-secondary"
                          disabled={qaBusy === 'recheck'}
                          title="Re-check and write back the mechanical format fixes (tables, formulas, stray markdown). No AI."
                          onClick={() => handleQaRecheck(true)}
                        >
                          <FontAwesomeIcon icon={faWandMagicSparkles} /> Fix formatting
                        </button>
                      </div>
                    </div>

                    {previewErr ? <div className="pp-pack-note">{previewErr}</div> : null}

                    {!(qaPanel?.issues || []).length ? (
                      <div className="pp-empty">
                        No open issues for this {qaPanel?.kind === 'mcq' ? 'MCQ set' : 'day'}.
                        <div className="pp-pages-hint" style={{ marginTop: '0.5rem' }}>
                          Use <strong>Re-check (free)</strong> after editing by hand.
                        </div>
                      </div>
                    ) : (
                      <ul className="pp-qa-issues">
                        {(qaPanel.issues || []).map((i) => (
                          <li key={i.id} className={`pp-qa-issue sev-${i.severity} st-${i.status}`}>
                            <div className="pp-qa-issue-head">
                              <span className={`pp-qa-sev pp-qa-sev-${i.severity}`}>{i.severity}</span>
                              <code className="pp-qa-code">{i.code}</code>
                              <span className={`pp-qa-status pp-qa-status-${i.status}`}>{String(i.status).replace(/_/g, ' ')}</span>
                              {i.fixAttempts > 0 ? <span className="pp-qa-attempts">{i.fixAttempts} fix attempt{i.fixAttempts === 1 ? '' : 's'}</span> : null}
                            </div>
                            <div className="pp-qa-issue-detail" dir="auto">{i.detail}</div>
                            {i.resolution ? <div className="pp-qa-issue-res">{i.resolution}</div> : null}
                            <div className="pp-qa-issue-actions">
                              {i.status === 'accepted' || i.status === 'wontfix' ? (
                                <button
                                  type="button"
                                  className="pp-btn pp-btn-secondary"
                                  disabled={qaBusy === `issue:${i.id}`}
                                  onClick={() => handleQaIssueStatus(i, 'open')}
                                >
                                  Reopen
                                </button>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="pp-btn pp-btn-secondary"
                                    disabled={qaBusy === `issue:${i.id}`}
                                    title="The content is correct as written — close this issue."
                                    onClick={() => handleQaIssueStatus(i, 'accepted')}
                                  >
                                    Checked, correct
                                  </button>
                                  <button
                                    type="button"
                                    className="pp-btn pp-btn-secondary"
                                    disabled={qaBusy === `issue:${i.id}`}
                                    title="Keep it as it is and never report this again."
                                    onClick={() => handleQaIssueStatus(i, 'wontfix')}
                                  >
                                    Keep as is
                                  </button>
                                </>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}

                    {(qaPanel?.blocks || []).length ? (
                      <div className="pp-qa-blocks">
                        <h4>
                          Questions in this {qaPanel.kind === 'mcq' ? 'MCQ set' : 'day'}
                          <span className="pp-qa-blocks-hint">
                            Lock an answer you have verified — later generations restore it exactly.
                          </span>
                        </h4>
                        <ul>
                          {qaPanel.blocks.map((b) => (
                            <li key={b.id} className={`pp-qa-block${b.approved ? ' approved' : ''}${b.issues?.length ? ' flagged' : ''}`}>
                              <div className="pp-qa-block-q" dir="auto">
                                {b.label ? <strong>{b.label} </strong> : null}
                                {b.question || '(no question text)'}
                              </div>
                              {b.issues?.length ? (
                                <div className="pp-qa-block-flags">
                                  {b.issues.map((bi) => (
                                    <span key={bi.id} className={`pp-qa-sev pp-qa-sev-${bi.severity}`}>{bi.code}</span>
                                  ))}
                                </div>
                              ) : null}
                              {b.approvedDrifted ? (
                                <div className="pp-qa-block-drift">
                                  Locked text differs from what is saved — it will be restored on the next run.
                                </div>
                              ) : null}
                              <button
                                type="button"
                                className={`pp-btn ${b.approved ? 'pp-btn-secondary' : 'pp-btn-primary'}`}
                                disabled={qaBusy === `block:${b.id}`}
                                title={b.approved
                                  ? 'Remove the lock so generation may change this answer again'
                                  : 'Freeze this answer — later generations restore this exact text'}
                                onClick={() => handleApproveBlock(b)}
                              >
                                {qaBusy === `block:${b.id}`
                                  ? <FontAwesomeIcon icon={faSpinner} spin />
                                  : b.approved
                                    ? <><FontAwesomeIcon icon={faXmark} /> Unlock</>
                                    : <><FontAwesomeIcon icon={faFloppyDisk} /> Lock as verified</>}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            ) : previewMode === 'ocr' ? (
              <div className={`pp-preview-doc pp-preview-ocr${looksUrdu(ocrEditing ? ocrDraft : previewText) ? ' rtl' : ''}`}>
                {reocrBusy ? (
                  <div className="pp-empty">
                    <FontAwesomeIcon icon={faSpinner} spin />{' '}
                    {reocrPageBusy
                      ? `Re-OCR p${reocrPageBusy}…`
                      : reocrScope === 'pages'
                        ? `Re-OCR pages ${reocrPagesInput.trim() || '…'}…`
                        : 'Re-OCR book — reading pages and yellow equation boxes…'}
                  </div>
                ) : ocrEditing ? (
                  <>
                    {ocrDirty ? (
                      <div className="pp-pack-note">Unsaved OCR edits — click Save before generating.</div>
                    ) : null}
                    <textarea
                      className="pp-ocr-editor"
                      value={ocrDraft}
                      onChange={(e) => setOcrDraft(e.target.value)}
                      spellCheck={false}
                      dir="auto"
                      placeholder="Paste or type corrected chapter OCR here…"
                      disabled={ocrSaving}
                    />
                  </>
                ) : previewErr && !previewText ? (
                  <div className="pp-empty">
                    {previewErr}
                    <div className="pp-pages-hint" style={{ marginTop: '0.75rem' }}>
                      Use <strong>Re-OCR book</strong> or enter <strong>p1, p2</strong> and click <strong>Re-OCR pages</strong>,
                      or click <strong>Edit OCR</strong> to type/paste text manually.
                    </div>
                  </div>
                ) : previewText ? (
                  <>
                    {previewErr ? <div className="pp-pack-note">{previewErr}</div> : null}
                    <pre className="pp-ocr-body">{previewText}</pre>
                  </>
                ) : (
                  <div className="pp-empty">
                    {previewErr || 'No OCR text for this chapter yet.'}
                    <div className="pp-pages-hint" style={{ marginTop: '0.75rem' }}>
                      Click <strong>Re-OCR book</strong>, or <strong>Edit OCR</strong> to enter text by hand.
                    </div>
                  </div>
                )}
              </div>
            ) : previewMode === 'pdf' && previewPdfUrl ? (
              <div className="pp-preview-doc pp-preview-pdf">
                <iframe
                  title={previewTitle || 'PDF preview'}
                  src={`${previewPdfUrl}#navpanes=0&scrollbar=1&view=FitH`}
                  className="pp-pdf-frame"
                />
              </div>
            ) : previewMode === 'shots' && previewShots.length ? (
              <div className="pp-preview-shots">
                {previewErr ? <div className="pp-pack-note">{previewErr}</div> : null}
                {previewShots.map((shot) => (
                  <figure key={shot.file} className="pp-shot-card">
                    <img
                      src={shot.url}
                      alt={shot.label}
                      loading="eager"
                      decoding="async"
                    />
                    <figcaption>{shot.label}</figcaption>
                  </figure>
                ))}
              </div>
            ) : (
              <div className={`pp-preview-doc${looksUrdu(previewText) ? ' rtl' : ''}`}>
                {previewErr && previewText ? (
                  <div className="pp-pack-note">{previewErr}</div>
                ) : null}
                {previewText ? (
                  <div ref={previewKey === 'full_notes' ? fullNotesCopyRef : undefined}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {isNotesAnswerPreview(previewKey) ? formatNotesAnswerPreview(previewText) : previewText}
                    </ReactMarkdown>
                  </div>
                ) : previewErr ? (
                  <div className="pp-empty">{previewErr}</div>
                ) : (
                  <div className="pp-empty">
                    Select a plan day under the chapter to preview notes or worksheets.
                    Use <strong>Run</strong> (One day / Notes — one day / MCQs — every day)
                    or the Day / Notes / MCQs buttons to generate only what you need.
                  </div>
                )}
              </div>
            )}
          </section>
        ) : (
          <div className="pp-preview-pane pp-preview-empty-state">
            <div className="pp-empty">Select a chapter in Plan overview to open its days and preview.</div>
          </div>
        )}
      </div>

      {lightboxPage?.blobUrl ? (
        <div
          className="pp-page-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`Page ${lightboxPage.page}`}
          onClick={() => setPageLightbox(null)}
        >
          <div className="pp-page-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <div className="pp-page-lightbox-bar">
              <span>
                p.{lightboxPage.page}
                {chapterPages.length > 1 ? ` · ${lightboxIdx + 1} / ${chapterPages.length}` : ''}
              </span>
              <button
                type="button"
                className="pp-page-lightbox-close"
                title="Close (Esc)"
                onClick={() => setPageLightbox(null)}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="pp-page-lightbox-stage">
              {chapterPages.length > 1 ? (
                <button
                  type="button"
                  className="pp-page-lightbox-nav"
                  title="Previous page (←)"
                  onClick={() => {
                    const prev = chapterPages[lightboxIdx - 1] || chapterPages[chapterPages.length - 1];
                    if (prev) setPageLightbox(prev.page);
                  }}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
              ) : null}
              <img
                src={lightboxPage.blobUrl}
                alt={`Page ${lightboxPage.page}`}
                className="pp-page-lightbox-img"
              />
              {chapterPages.length > 1 ? (
                <button
                  type="button"
                  className="pp-page-lightbox-nav"
                  title="Next page (→)"
                  onClick={() => {
                    const next = chapterPages[lightboxIdx + 1] || chapterPages[0];
                    if (next) setPageLightbox(next.page);
                  }}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
